import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import {
  DndContext,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { createSnapModifier } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AnimatePresence, motion } from 'framer-motion';
import {
  type AiFeedbackEntry,
  type AiFeedbackSignal,
  type AiFeedbackTargetType,
  type CallRecord,
  type ContactListItem,
  type ConversationEvent,
  type ConversationEventType,
  type Message,
  type RagSuggestion,
  type TelephonyRoutingReadiness,
} from '@callhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button, Input, TextArea } from '@/components/ui-next';
import {
  RoutingReadinessStrip,
  type ReadinessStatusTone,
} from '@/components/RoutingReadinessStrip';
import { api } from '@/lib/api';
import {
  DIALER_WIDGET_DRAG_LOCK_EVENT,
  DIALER_WIDGET_MODE_EVENT,
  normalizeDialerWidgetMode,
} from '@/lib/dialer-widget-mode';
import {
  acceptOrHangupCurrentCall,
  ensureTwilioVoiceDevice,
  getTwilioVoiceState,
  sendDigitsOnActiveCall,
  startOutboundCall,
  subscribeTwilioVoice,
  type VoiceCallStatus,
  type VoiceQualityLevel,
} from '@/lib/twilio-voice';
import {
  DEFAULT_LEFT_PANEL_PERCENT,
  MAX_LEFT_PANEL_PERCENT_THREE_COLUMNS,
  MAX_LEFT_PANEL_PERCENT_TWO_COLUMNS,
  clampSplitPercent,
  getSplitPercentFromPointer,
} from './dialer-split';
import {
  isLikelyResolvablePhone,
  sanitizeDialerInput,
  shouldShowContactsSearchHint,
} from './dialer-input';
import { shouldShowColumnDropHint, shouldShowSummaryRefresh } from './dialer-ai-controls';
import {
  DEFAULT_WIDGET_LAYOUT,
  normalizeWidgetLayout,
  type DialerWidgetContainerId,
  type DialerWidgetId,
  type DialerWidgetLayout,
} from './dialer-widgets-layout';
import {
  DEFAULT_BELOW_MESSAGES_LAYOUT,
  normalizeBelowMessagesLayout,
  type DialerBelowMessagesContainerId,
  type DialerBelowMessagesLayout,
  type DialerMessagesWidgetMode,
} from './dialer-below-messages-layout';
import {
  type DialerHybridWidgetMode,
  type DialerClassicTwoColumnsLayout,
  getMessageAreaColumnIds,
  moveClassicTwoColumnsWidgetInLayout,
  normalizeClassicTwoColumnsLayout,
  rebalanceClassicTwoColumnsLayout,
  resolveClassicTwoColumnsContainer,
  splitClassicTwoColumnsLayout,
} from './dialer-classic-two-columns-layout';
import { resolveDialerLivePollingProfile } from './dialer-live-polling';
import { resolveDialerLiveTransportStatus } from './dialer-live-transport';
import { resolveTranscriptLiveHealth } from './dialer-transcript-health';
import { mergeConversationEventTimeline, resolveDialerEventFetchPlan } from './dialer-event-sync';
import { startDialerLiveFanoutSession, type DialerLiveFanoutState } from './dialer-live-fanout';

type InsightPanelKey = 'transcript' | 'actionItems' | 'summary' | 'highlights' | 'suggestions';
type DialerCollapsibleSectionKey = InsightPanelKey | 'callQuality' | 'callHistory';
type DialerHideableSectionKey =
  | 'connectionStatus'
  | 'callQuality'
  | 'transcript'
  | 'actionItems'
  | 'callHistory'
  | 'summary'
  | 'highlights'
  | 'suggestions';
type WidgetPanelKey = Extract<
  InsightPanelKey,
  'transcript' | 'actionItems' | 'summary' | 'highlights' | 'suggestions'
>;
type DialerUiPreferences = {
  widgetLayout?: Partial<Record<DialerWidgetContainerId, string[]>>;
  belowMessagesLayout?: Partial<Record<DialerBelowMessagesContainerId, string[]>>;
  messagesWidgetMode?: DialerMessagesWidgetMode;
  modeLayouts?: Partial<Record<DialerWidgetViewMode, unknown>>;
  widgetDragLocked?: boolean;
  collapsedSections?: Partial<Record<DialerCollapsibleSectionKey, boolean>>;
  hiddenSections?: Partial<Record<DialerHideableSectionKey, boolean>>;
};
type DialerUiStoragePreferences = DialerUiPreferences & {
  localUpdatedAtMs?: number;
};
type DialerWidgetViewMode = Exclude<DialerMessagesWidgetMode, 'classic'>;
type DialerModeLayoutSnapshot = {
  widgetLayout: DialerWidgetLayout;
  belowMessagesLayout: DialerBelowMessagesLayout;
};
type DialerModeLayouts = Partial<Record<DialerWidgetViewMode, DialerModeLayoutSnapshot>>;
type SplitResizeTarget = 'left' | 'right' | null;

const KEYPAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
const DIALER_UI_PREFERENCE_KEY = 'dialer_ui';
const DIALER_UI_STORAGE_KEY = 'callhub:dialer-ui:v1';
const LEGACY_WIDGET_LAYOUT_STORAGE_KEY = 'callhub:dialer-widgets:v1';
const DIALER_PREFERENCE_SYNC_ERROR_MESSAGE =
  'Could not sync dialer layout to server. Changes are saved locally and will retry automatically.';
const DIALER_PREFERENCE_RETRY_DELAY_MS = 2_000;
const DIALER_WIDGET_VIEW_MODES: DialerWidgetViewMode[] = ['two_columns', 'three_columns'];
const DEFAULT_RIGHT_PANEL_PERCENT = 20;
const MIN_PARENT_COLUMN_PERCENT = 20;
const MIN_CENTER_PANEL_PERCENT = 20;
const DEFAULT_COLLAPSED_SECTIONS: Record<DialerCollapsibleSectionKey, boolean> = {
  transcript: false,
  actionItems: false,
  summary: false,
  highlights: false,
  suggestions: false,
  callQuality: false,
  callHistory: false,
};
const DEFAULT_HIDDEN_SECTIONS: Record<DialerHideableSectionKey, boolean> = {
  connectionStatus: false,
  callQuality: false,
  transcript: false,
  actionItems: false,
  callHistory: false,
  summary: false,
  highlights: false,
  suggestions: false,
};
const HIDEABLE_SECTION_LABELS: Record<DialerHideableSectionKey, string> = {
  connectionStatus: 'Connection Status',
  callQuality: 'Call Quality',
  transcript: 'Voice Transcript',
  actionItems: 'Action Items',
  callHistory: 'Call History',
  summary: 'AI Summary',
  highlights: 'Conversation Highlights',
  suggestions: 'Suggestions',
};
const PAGE_PREFERENCES_ORDER: DialerHideableSectionKey[] = [
  'connectionStatus',
  'callQuality',
  'transcript',
  'actionItems',
  'callHistory',
  'summary',
  'highlights',
  'suggestions',
];
const INSIGHT_EVENT_TYPE_MAP: Record<AiFeedbackTargetType, ConversationEventType> = {
  summary: 'summary',
  highlight: 'highlight',
  action_item: 'action_item',
  voice_transcript: 'voice_transcript',
};
const WIDGET_TO_PANEL_KEY: Record<DialerWidgetId, WidgetPanelKey> = {
  voice_transcript: 'transcript',
  action_items: 'actionItems',
  ai_summary: 'summary',
  conversation_highlights: 'highlights',
  rag_suggestions: 'suggestions',
};
const WIDGET_TO_HIDEABLE_SECTION_KEY: Record<DialerWidgetId, DialerHideableSectionKey> = {
  voice_transcript: 'transcript',
  action_items: 'actionItems',
  ai_summary: 'summary',
  conversation_highlights: 'highlights',
  rag_suggestions: 'suggestions',
};
const AI_CARD_CLASS =
  'rounded-lg border border-amber-300/65 bg-amber-50/85 p-2 sm:p-2.5 dark:border-amber-500/25 dark:bg-amber-950/25';
const AI_CARD_HEADER_BUTTON_CLASS =
  'flex min-w-0 grow basis-[11rem] items-center gap-1.5 text-left text-amber-900 transition-colors hover:text-amber-700 sm:gap-2 dark:text-amber-200 dark:hover:text-amber-100';
const AI_CARD_ITEM_CLASS =
  'rounded-md border border-amber-300/50 bg-amber-100/55 dark:border-amber-500/20 dark:bg-background/80';
const AI_REFRESH_BUTTON_CLASS =
  'h-6 rounded border border-amber-400/60 bg-amber-100/80 px-2 text-xs text-amber-900 transition-colors hover:bg-amber-200/80 hover:text-amber-900 sm:h-7 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100 dark:hover:bg-amber-500/20 dark:hover:text-amber-50 disabled:opacity-50';

type NetworkReadinessCheck = {
  id: string;
  label: string;
  ok: boolean;
  required: boolean;
};

type NetworkReadinessState = {
  checks: NetworkReadinessCheck[];
  ready: boolean;
  connectionLabel: string;
};

function getBrowserWindow(): Window | null {
  return typeof window === 'undefined' ? null : window;
}

function getBrowserDocument(): Document | null {
  return typeof document === 'undefined' ? null : document;
}

type DialerLiveTransportStatus = ReturnType<typeof resolveDialerLiveTransportStatus>['status'];
type TranscriptLiveHealthStatus = ReturnType<typeof resolveTranscriptLiveHealth>['status'];

function resolveLiveTransportTone(status: DialerLiveTransportStatus): ReadinessStatusTone {
  if (status === 'live') return 'good';
  if (status === 'retrying') return 'watch';
  return 'neutral';
}

function resolveTranscriptHealthTone(status: TranscriptLiveHealthStatus): ReadinessStatusTone {
  if (status === 'live') return 'good';
  if (status === 'stale' || status === 'missing') return 'critical';
  return 'neutral';
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;

  if (target.isContentEditable) return true;
  if (target.closest('[contenteditable="true"]')) return true;
  if (target.closest('input, textarea, select, button')) return true;

  return false;
}

type FeedbackControlsProps = {
  disabled: boolean;
  signal: AiFeedbackSignal | null;
  onFeedback: (signal: AiFeedbackSignal) => void;
};

function FeedbackControls({ disabled, signal, onFeedback }: FeedbackControlsProps) {
  return (
    <div className="inline-flex items-center rounded-full border border-border/80 bg-background/80 p-0.5 shadow-sm backdrop-blur">
      <button
        type="button"
        onClick={() => onFeedback('up')}
        aria-pressed={signal === 'up'}
        disabled={disabled}
        className={`inline-flex h-6 min-w-7 items-center justify-center rounded-full px-1 transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-500 sm:h-7 sm:min-w-8 ${
          signal === 'up'
            ? 'bg-emerald-500/20 text-emerald-700 shadow-[inset_0_0_0_1px_rgba(16,185,129,0.35)] dark:text-emerald-300'
            : 'text-muted-foreground hover:bg-emerald-500/10 hover:text-emerald-700 dark:hover:text-emerald-300'
        } disabled:cursor-not-allowed disabled:opacity-40`}
        title="Mark as helpful"
      >
        <span className="material-symbols-outlined text-[13px] sm:text-[15px]">thumb_up</span>
      </button>
      <button
        type="button"
        onClick={() => onFeedback('down')}
        aria-pressed={signal === 'down'}
        disabled={disabled}
        className={`inline-flex h-6 min-w-7 items-center justify-center rounded-full px-1 transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-500 sm:h-7 sm:min-w-8 ${
          signal === 'down'
            ? 'bg-rose-500/20 text-rose-700 shadow-[inset_0_0_0_1px_rgba(244,63,94,0.35)] dark:text-rose-300'
            : 'text-muted-foreground hover:bg-rose-500/10 hover:text-rose-700 dark:hover:text-rose-300'
        } disabled:cursor-not-allowed disabled:opacity-40`}
        title="Mark as not helpful"
      >
        <span className="material-symbols-outlined text-[13px] sm:text-[15px]">thumb_down</span>
      </button>
    </div>
  );
}

type SortableWidgetShellProps = {
  widgetId: DialerWidgetId;
  dragLocked: boolean;
  children: (dragHandleProps: HTMLAttributes<HTMLButtonElement>) => ReactNode;
};

function SortableWidgetShell({ widgetId, dragLocked, children }: SortableWidgetShellProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: widgetId,
    disabled: dragLocked,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={isDragging ? 'z-30 opacity-90' : ''}
    >
      {children(dragLocked ? {} : { ...attributes, ...listeners })}
    </motion.div>
  );
}

type WidgetColumnProps = {
  columnId: string;
  items: DialerWidgetId[];
  showDropHint: boolean;
  children: ReactNode;
};

function WidgetColumn({ columnId, items, showDropHint, children }: WidgetColumnProps) {
  const { isOver, setNodeRef } = useDroppable({ id: columnId });

  return (
    <div
      ref={setNodeRef}
      data-widget-column-id={columnId}
      className={`space-y-1 rounded-lg border border-dashed p-0.5 transition-colors sm:space-y-1.5 sm:p-1 ${
        isOver ? 'border-brand-400/60 bg-brand-500/5' : 'border-transparent'
      }`}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <AnimatePresence initial={false}>{children}</AnimatePresence>
      </SortableContext>
      {items.length === 0 && showDropHint ? (
        <p className="rounded-lg border border-border bg-background/70 px-3 py-2 text-xs text-muted-foreground">
          Drop blocks here
        </p>
      ) : null}
    </div>
  );
}

function formatTimestamp(input: string | Date | number) {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function buildFallbackSummary(events: ConversationEvent[]): string {
  const smsCount = events.filter(
    (event) => event.eventType === 'sms_inbound' || event.eventType === 'sms_outbound',
  ).length;
  const callCount = events.filter((event) => event.eventType === 'call_started').length;

  return `Session activity: ${smsCount} SMS and ${callCount} calls.`;
}

function pickFallbackHighlights(events: ConversationEvent[]): string[] {
  return events
    .filter(
      (event) =>
        event.eventType === 'sms_inbound' ||
        event.eventType === 'sms_outbound' ||
        event.eventType === 'call_started' ||
        event.eventType === 'call_ended',
    )
    .map((event) => {
      if (event.body) return event.body;
      if (event.eventType === 'call_started') return 'Voice call started';
      if (event.eventType === 'call_ended') return 'Voice call ended';
      return '';
    })
    .filter((item) => item.length > 0)
    .slice(0, 3);
}

function buildFeedbackKey(targetType: AiFeedbackTargetType, eventId?: string | null) {
  return `${targetType}:${eventId ?? 'none'}`;
}

function qualityLevelLabel(level: VoiceQualityLevel): string {
  if (level === 'excellent') return 'Excellent';
  if (level === 'good') return 'Good';
  if (level === 'fair') return 'Fair';
  if (level === 'poor') return 'Poor';
  return 'Unknown';
}

function qualityLevelClass(level: VoiceQualityLevel): string {
  if (level === 'excellent') return 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300';
  if (level === 'good') return 'bg-brand-500/20 text-brand-700 dark:text-brand-300';
  if (level === 'fair') return 'bg-amber-500/20 text-amber-700 dark:text-amber-300';
  if (level === 'poor') return 'bg-red-500/20 text-red-700 dark:text-red-300';
  return 'bg-muted text-muted-foreground';
}

function formatMetric(value: number | null, digits: number = 1): string {
  if (value === null || Number.isNaN(value)) return '—';
  return value.toFixed(digits);
}

function normalizeCollapsedSections(
  raw: Partial<Record<DialerCollapsibleSectionKey, boolean>> | null | undefined,
): Record<DialerCollapsibleSectionKey, boolean> {
  if (!raw) return DEFAULT_COLLAPSED_SECTIONS;
  return {
    transcript: raw.transcript === true,
    actionItems: raw.actionItems === true,
    summary: raw.summary === true,
    highlights: raw.highlights === true,
    suggestions: raw.suggestions === true,
    callQuality: raw.callQuality === true,
    callHistory: raw.callHistory === true,
  };
}

function normalizeHiddenSections(
  raw: Partial<Record<DialerHideableSectionKey, boolean>> | null | undefined,
): Record<DialerHideableSectionKey, boolean> {
  if (!raw) return DEFAULT_HIDDEN_SECTIONS;
  return {
    connectionStatus: raw.connectionStatus === true,
    callQuality: raw.callQuality === true,
    transcript: raw.transcript === true,
    actionItems: raw.actionItems === true,
    callHistory: raw.callHistory === true,
    summary: raw.summary === true,
    highlights: raw.highlights === true,
    suggestions: raw.suggestions === true,
  };
}

function createModeLayoutSnapshot(
  widgetLayout: Partial<Record<DialerWidgetContainerId, string[]>> | null | undefined,
  belowMessagesLayout: Partial<Record<DialerBelowMessagesContainerId, string[]>> | null | undefined,
): DialerModeLayoutSnapshot {
  const normalizedLayout = normalizeClassicTwoColumnsLayout(widgetLayout, belowMessagesLayout);
  return splitClassicTwoColumnsLayout(normalizedLayout);
}

function normalizeDialerModeLayoutSnapshot(raw: unknown): DialerModeLayoutSnapshot | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (!('widgetLayout' in raw) && !('belowMessagesLayout' in raw)) return null;

  const parsed = raw as {
    widgetLayout?: Partial<Record<DialerWidgetContainerId, string[]>>;
    belowMessagesLayout?: Partial<Record<DialerBelowMessagesContainerId, string[]>>;
  };

  return createModeLayoutSnapshot(parsed.widgetLayout, parsed.belowMessagesLayout);
}

function normalizeModeLayouts(
  raw: Partial<Record<DialerWidgetViewMode, unknown>> | null | undefined,
) {
  const next: DialerModeLayouts = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return next;

  for (const mode of DIALER_WIDGET_VIEW_MODES) {
    const snapshot = normalizeDialerModeLayoutSnapshot(raw[mode]);
    if (!snapshot) continue;
    next[mode] = snapshot;
  }

  return next;
}

type ParsedDialerUiPreferences = {
  widgetLayout: DialerWidgetLayout;
  belowMessagesLayout: DialerBelowMessagesLayout;
  messagesWidgetMode: DialerWidgetViewMode;
  modeLayouts: DialerModeLayouts;
  widgetDragLocked: boolean;
  collapsedSections: Record<DialerCollapsibleSectionKey, boolean>;
  hiddenSections: Record<DialerHideableSectionKey, boolean>;
};

function parseDialerUiPreferences(raw: unknown): ParsedDialerUiPreferences | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const parsedRecord = raw as Record<string, unknown>;
  const hasStructuredFields =
    'widgetLayout' in parsedRecord ||
    'belowMessagesLayout' in parsedRecord ||
    'messagesWidgetMode' in parsedRecord ||
    'modeLayouts' in parsedRecord ||
    'widgetDragLocked' in parsedRecord ||
    'collapsedSections' in parsedRecord ||
    'hiddenSections' in parsedRecord;
  if (!hasStructuredFields) {
    const legacy = raw as Partial<Record<DialerWidgetContainerId, string[]>>;
    return {
      widgetLayout: normalizeWidgetLayout(legacy),
      belowMessagesLayout: DEFAULT_BELOW_MESSAGES_LAYOUT,
      messagesWidgetMode: 'two_columns',
      modeLayouts: {},
      widgetDragLocked: false,
      collapsedSections: DEFAULT_COLLAPSED_SECTIONS,
      hiddenSections: DEFAULT_HIDDEN_SECTIONS,
    };
  }

  const parsed = raw as DialerUiPreferences;
  return {
    widgetLayout: normalizeWidgetLayout(parsed.widgetLayout),
    belowMessagesLayout: normalizeBelowMessagesLayout(parsed.belowMessagesLayout),
    messagesWidgetMode: normalizeDialerWidgetMode(parsed.messagesWidgetMode),
    modeLayouts: normalizeModeLayouts(parsed.modeLayouts),
    widgetDragLocked: parsed.widgetDragLocked === true,
    collapsedSections: normalizeCollapsedSections(parsed.collapsedSections),
    hiddenSections: normalizeHiddenSections(parsed.hiddenSections),
  };
}

function toHybridWidgetMode(mode: DialerWidgetViewMode): DialerHybridWidgetMode {
  return mode === 'three_columns' ? 'three_columns' : 'two_columns';
}

function getAutoLeftPanelPercent(mode: DialerWidgetViewMode): number {
  return mode === 'three_columns' ? 24 : 30;
}

function getMaxLeftPanelPercent(mode: DialerWidgetViewMode): number {
  return mode === 'three_columns'
    ? MAX_LEFT_PANEL_PERCENT_THREE_COLUMNS
    : MAX_LEFT_PANEL_PERCENT_TWO_COLUMNS;
}

function getMaxRightPanelPercent(leftPanelPercent: number): number {
  return Math.max(MIN_PARENT_COLUMN_PERCENT, 100 - leftPanelPercent - MIN_CENTER_PANEL_PERCENT);
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || Number.isNaN(seconds) || seconds < 0) return '—';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

type AuthenticatedRecordingPlayerProps = {
  callId: string;
};

function AuthenticatedRecordingPlayer({ callId }: AuthenticatedRecordingPlayerProps) {
  const [recordingBlobUrl, setRecordingBlobUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    setStatus('loading');
    setRecordingBlobUrl(null);

    void (async () => {
      try {
        const blob = await api.getCallRecordingBlob(callId);
        if (blob.size <= 0) {
          throw new Error('Recording payload is empty');
        }

        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        setRecordingBlobUrl(objectUrl);
        setStatus('ready');
      } catch (error) {
        if (cancelled) return;
        console.warn('Failed to load authenticated recording blob for call history playback', {
          callId,
          error: error instanceof Error ? error.message : String(error),
        });
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [callId]);

  if (status === 'loading') {
    return <p className="mt-2 text-xs text-muted-foreground">Loading recording...</p>;
  }

  if (status === 'error' || !recordingBlobUrl) {
    return <p className="mt-2 text-xs text-muted-foreground">Recording unavailable</p>;
  }

  return <audio className="mt-2 w-full" controls preload="none" src={recordingBlobUrl} />;
}

function getCallRecordStatusClass(status: CallRecord['status']): string {
  if (status === 'completed') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  if (status === 'in_progress') return 'bg-brand-500/15 text-brand-700 dark:text-brand-300';
  if (status === 'ringing') return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
  return 'bg-red-500/15 text-red-700 dark:text-red-300';
}

function formatRagSuggestionStatus(status: RagSuggestion['status']): string {
  if (status === 'applied') return 'Applied';
  if (status === 'accepted') return 'Accepted';
  if (status === 'rejected') return 'Rejected';
  return 'Proposed';
}

function getRagSuggestionStatusClass(status: RagSuggestion['status']): string {
  if (status === 'applied') return 'bg-brand-500/20 text-brand-700 dark:text-brand-300';
  if (status === 'accepted') return 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300';
  if (status === 'rejected') return 'bg-rose-500/20 text-rose-700 dark:text-rose-300';
  return 'bg-muted text-muted-foreground';
}

function buildNetworkReadinessState(isRegistered: boolean): NetworkReadinessState {
  const currentWindow = getBrowserWindow();
  const currentNavigator = typeof navigator === 'undefined' ? null : navigator;

  if (!currentWindow || !currentNavigator) {
    return {
      checks: [
        {
          id: 'twilio-registration',
          label: 'Twilio voice device registered',
          ok: isRegistered,
          required: true,
        },
      ],
      ready: isRegistered,
      connectionLabel: 'Unknown',
    };
  }

  const nav = currentNavigator as Navigator & {
    connection?: {
      effectiveType?: string;
      saveData?: boolean;
    };
    mozConnection?: {
      effectiveType?: string;
      saveData?: boolean;
    };
    webkitConnection?: {
      effectiveType?: string;
      saveData?: boolean;
    };
  };

  const connection = nav.connection ?? nav.mozConnection ?? nav.webkitConnection ?? null;
  const effectiveType = connection?.effectiveType?.toUpperCase() ?? 'UNKNOWN';
  const connectionLabel =
    effectiveType !== 'UNKNOWN' ? effectiveType : currentNavigator.onLine ? 'Online' : 'Offline';
  const connectionHealthy =
    connection?.saveData === true ||
    connection?.effectiveType === 'slow-2g' ||
    connection?.effectiveType === '2g'
      ? false
      : true;

  const checks: NetworkReadinessCheck[] = [
    {
      id: 'online',
      label: 'Browser online',
      ok: currentNavigator.onLine,
      required: true,
    },
    {
      id: 'secure-context',
      label: 'Secure context (HTTPS)',
      ok: currentWindow.isSecureContext,
      required: true,
    },
    {
      id: 'webrtc',
      label: 'WebRTC support',
      ok: typeof RTCPeerConnection !== 'undefined',
      required: true,
    },
    {
      id: 'microphone-api',
      label: 'Microphone API available',
      ok: Boolean(currentNavigator.mediaDevices?.getUserMedia),
      required: true,
    },
    {
      id: 'twilio-registration',
      label: 'Twilio voice device registered',
      ok: isRegistered,
      required: true,
    },
    {
      id: 'connection-profile',
      label: 'Connection profile is not constrained',
      ok: connectionHealthy,
      required: false,
    },
  ];

  const ready = checks.every((check) => !check.required || check.ok);
  return { checks, ready, connectionLabel };
}

export function DialerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [voiceState, setVoiceState] = useState(getTwilioVoiceState);
  const { isRegistered, activeCall, callStatus, callQuality } = voiceState;
  const hasActiveCall = Boolean(activeCall);
  const isInboundRinging = callStatus === 'ringing' && voiceState.activeCallDirection === 'inbound';
  const livePollingProfile = useMemo(
    () => resolveDialerLivePollingProfile({ callStatus, hasActiveCall }),
    [callStatus, hasActiveCall],
  );
  const [smsBody, setSmsBody] = useState('');
  const [ragPrompt, setRagPrompt] = useState('');
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [editContactName, setEditContactName] = useState('');
  const [editContactPhone, setEditContactPhone] = useState('');
  const [editContactEmail, setEditContactEmail] = useState('');
  const [isPhoneInputFocused, setIsPhoneInputFocused] = useState(false);
  const [leftPanelPercent, setLeftPanelPercent] = useState(DEFAULT_LEFT_PANEL_PERCENT);
  const [rightPanelPercent, setRightPanelPercent] = useState(DEFAULT_RIGHT_PANEL_PERCENT);
  const [splitResizeTarget, setSplitResizeTarget] = useState<SplitResizeTarget>(null);
  const [isDesktopLayout, setIsDesktopLayout] = useState(() => {
    const currentWindow = getBrowserWindow();
    return !currentWindow || currentWindow.innerWidth >= 1024;
  });
  const [collapsedSections, setCollapsedSections] = useState<
    Record<DialerCollapsibleSectionKey, boolean>
  >(DEFAULT_COLLAPSED_SECTIONS);
  const [hiddenSections, setHiddenSections] =
    useState<Record<DialerHideableSectionKey, boolean>>(DEFAULT_HIDDEN_SECTIONS);
  const [widgetLayout, setWidgetLayout] = useState<DialerWidgetLayout>(DEFAULT_WIDGET_LAYOUT);
  const [belowMessagesLayout, setBelowMessagesLayout] = useState<DialerBelowMessagesLayout>(
    DEFAULT_BELOW_MESSAGES_LAYOUT,
  );
  const [messagesWidgetMode, setMessagesWidgetMode] = useState<DialerWidgetViewMode>('two_columns');
  const [isWidgetDragLocked, setIsWidgetDragLocked] = useState(false);
  const [dialerPreferenceSyncWarning, setDialerPreferenceSyncWarning] = useState<string | null>(
    null,
  );
  const [modeLayouts, setModeLayouts] = useState<DialerModeLayouts>({});
  const [activeDraggedWidget, setActiveDraggedWidget] = useState<DialerWidgetId | null>(null);
  const [networkProbeTick, setNetworkProbeTick] = useState(0);
  const [autoCallError, setAutoCallError] = useState<string | null>(null);
  const [liveFanoutState, setLiveFanoutState] = useState<DialerLiveFanoutState>('closed');
  const [hasRecentRealtimeTail, setHasRecentRealtimeTail] = useState(false);

  const phoneNumber = sanitizeDialerInput(searchParams.get('phone') ?? '');
  const seededConversationId = searchParams.get('conversation');
  const seededContactId = searchParams.get('contact');
  const dialerAction = (searchParams.get('action') ?? '').trim().toLowerCase();
  const dialTarget = phoneNumber.trim();
  const canResolveSession = isLikelyResolvablePhone(dialTarget);

  const feedEndRef = useRef<HTMLDivElement>(null);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const widgetLayoutRef = useRef(widgetLayout);
  const belowMessagesLayoutRef = useRef(belowMessagesLayout);
  const messagesWidgetModeRef = useRef(messagesWidgetMode);
  const modeLayoutsRef = useRef(modeLayouts);
  const widgetDragLockedRef = useRef(isWidgetDragLocked);
  const collapsedSectionsRef = useRef(collapsedSections);
  const hiddenSectionsRef = useRef(hiddenSections);
  const hasHydratedDialerPrefsRef = useRef(false);
  const lastSavedDialerPrefsRef = useRef<string | null>(null);
  const pendingDialerPrefsTimerRef = useRef<number | null>(null);
  const pendingDialerPrefsRetryTimerRef = useRef<number | null>(null);
  const flushPendingDialerPreferenceSaveRef = useRef<(keepalive?: boolean) => void>(() => {});
  const pendingDialerPrefsRef = useRef<{
    payload: DialerUiPreferences;
    serialized: string;
  } | null>(null);
  const isDialerPrefsSaveInFlightRef = useRef(false);
  const activeConversationIdRef = useRef<string | null>(null);
  const dialTargetRef = useRef('');
  const previousVoiceSnapshotRef = useRef<{
    status: VoiceCallStatus;
    direction: 'inbound' | 'outbound' | null;
  }>({
    status: callStatus,
    direction: voiceState.activeCallDirection,
  });
  const previousVoiceStatusForRefreshRef = useRef<VoiceCallStatus>(callStatus);
  const eventsPollsSinceFullSyncRef = useRef(0);
  const realtimeTailUntilRef = useRef(0);
  const deltaEmptyPollsRef = useRef(0);
  const autoCallHandledRef = useRef(false);
  const lastKnownEventCursorRef = useRef<string | null>(null);
  const liveFanoutVoiceStateRef = useRef({
    callStatus,
    hasActiveCall,
  });
  const recentRealtimeTailRef = useRef(hasRecentRealtimeTail);
  const pendingAppliedSuggestionRef = useRef<{
    suggestionId: string;
    conversationId: string;
  } | null>(null);
  const inboundBrowserNotificationRef = useRef<Notification | null>(null);

  const dndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );
  const snapToGrid = useMemo(() => createSnapModifier(12), []);

  const setPhoneNumber = useCallback(
    (value: string, replace: boolean = true) => {
      const next = new URLSearchParams(searchParams);
      const sanitized = sanitizeDialerInput(value);
      if (sanitized) {
        next.set('phone', sanitized);
      } else {
        next.delete('phone');
      }
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace });
      }
    },
    [searchParams, setSearchParams],
  );

  const toggleSection = useCallback((section: DialerCollapsibleSectionKey) => {
    setCollapsedSections((current) => {
      const next = {
        ...current,
        [section]: !current[section],
      };
      collapsedSectionsRef.current = next;
      return next;
    });
  }, []);

  const toggleHiddenSection = useCallback((section: DialerHideableSectionKey) => {
    setHiddenSections((current) => {
      const next = {
        ...current,
        [section]: !current[section],
      };
      hiddenSectionsRef.current = next;
      return next;
    });
  }, []);

  const profilePreferencesQuery = useQuery({
    queryKey: ['me-preferences'],
    queryFn: async () => {
      const res = await api.getMyPreferences();
      return res.data;
    },
    staleTime: 120_000,
    refetchOnWindowFocus: false,
  });

  const flushPendingDialerPreferenceSave = useCallback((keepalive: boolean = false) => {
    if (pendingDialerPrefsTimerRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(pendingDialerPrefsTimerRef.current);
      pendingDialerPrefsTimerRef.current = null;
    }
    if (pendingDialerPrefsRetryTimerRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(pendingDialerPrefsRetryTimerRef.current);
      pendingDialerPrefsRetryTimerRef.current = null;
    }

    const fallbackPayload: DialerUiPreferences = {
      widgetLayout: widgetLayoutRef.current,
      belowMessagesLayout: belowMessagesLayoutRef.current,
      messagesWidgetMode: messagesWidgetModeRef.current,
      modeLayouts: modeLayoutsRef.current,
      widgetDragLocked: widgetDragLockedRef.current,
      collapsedSections: collapsedSectionsRef.current,
      hiddenSections: hiddenSectionsRef.current,
    };
    const fallbackSerialized = JSON.stringify(fallbackPayload);
    const hasUnsavedFallback = fallbackSerialized !== lastSavedDialerPrefsRef.current;

    if (pendingDialerPrefsRef.current) {
      if (hasUnsavedFallback && pendingDialerPrefsRef.current.serialized !== fallbackSerialized) {
        pendingDialerPrefsRef.current = {
          payload: fallbackPayload,
          serialized: fallbackSerialized,
        };
      }
    } else if (hasUnsavedFallback) {
      pendingDialerPrefsRef.current = {
        payload: fallbackPayload,
        serialized: fallbackSerialized,
      };
    }

    if (isDialerPrefsSaveInFlightRef.current || !pendingDialerPrefsRef.current) return;

    void (async () => {
      let shouldUseKeepalive = keepalive;

      while (pendingDialerPrefsRef.current && !isDialerPrefsSaveInFlightRef.current) {
        const nextPending = pendingDialerPrefsRef.current;
        pendingDialerPrefsRef.current = null;
        isDialerPrefsSaveInFlightRef.current = true;

        try {
          if (shouldUseKeepalive) {
            await api.saveMyPreference(
              {
                key: DIALER_UI_PREFERENCE_KEY,
                value: nextPending.payload,
              },
              { keepalive: true },
            );
          } else {
            await api.saveMyPreference({
              key: DIALER_UI_PREFERENCE_KEY,
              value: nextPending.payload,
            });
          }
          lastSavedDialerPrefsRef.current = nextPending.serialized;
          setDialerPreferenceSyncWarning(null);
        } catch {
          setDialerPreferenceSyncWarning(DIALER_PREFERENCE_SYNC_ERROR_MESSAGE);
          if (!pendingDialerPrefsRef.current) {
            pendingDialerPrefsRef.current = nextPending;
          }
          if (
            !shouldUseKeepalive &&
            pendingDialerPrefsRetryTimerRef.current === null &&
            typeof window !== 'undefined'
          ) {
            pendingDialerPrefsRetryTimerRef.current = window.setTimeout(() => {
              pendingDialerPrefsRetryTimerRef.current = null;
              flushPendingDialerPreferenceSaveRef.current(false);
            }, DIALER_PREFERENCE_RETRY_DELAY_MS);
          }
          break;
        } finally {
          isDialerPrefsSaveInFlightRef.current = false;
        }

        shouldUseKeepalive = false;
      }
    })();
  }, []);

  useEffect(() => {
    flushPendingDialerPreferenceSaveRef.current = flushPendingDialerPreferenceSave;
  }, [flushPendingDialerPreferenceSave]);

  const voiceAvailabilityQuery = useQuery({
    queryKey: ['me', 'voice-availability'],
    queryFn: async () => {
      const res = await api.getMyVoiceAvailability();
      return res.data;
    },
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });

  const routingReadinessQuery = useQuery({
    queryKey: ['team', 'integrations', 'routing-readiness'],
    queryFn: async (): Promise<TelephonyRoutingReadiness | null> => {
      const res = await api.getTeamRoutingReadiness();
      return res.data ?? null;
    },
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

  const saveVoiceAvailabilityMutation = useMutation({
    mutationFn: async (manualAvailability: boolean) =>
      api.saveMyVoiceAvailability({
        manualAvailability,
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(['me', 'voice-availability'], response.data);
    },
  });

  useEffect(() => {
    if (hasHydratedDialerPrefsRef.current || profilePreferencesQuery.isPending) return;

    let nextWidgetLayout = DEFAULT_WIDGET_LAYOUT;
    let nextBelowMessagesLayout = DEFAULT_BELOW_MESSAGES_LAYOUT;
    let nextMessagesWidgetMode: DialerWidgetViewMode = 'two_columns';
    let nextModeLayouts: DialerModeLayouts = {};
    let nextWidgetDragLocked = false;
    let nextCollapsedSections = DEFAULT_COLLAPSED_SECTIONS;
    let nextHiddenSections = DEFAULT_HIDDEN_SECTIONS;

    const dialerPrefEntry = profilePreferencesQuery.data?.find(
      (entry) => entry.key === DIALER_UI_PREFERENCE_KEY,
    );
    const serverPreference = parseDialerUiPreferences(dialerPrefEntry?.value);

    let localPreference: ParsedDialerUiPreferences | null = null;
    const currentWindow = getBrowserWindow();
    if (currentWindow) {
      const raw =
        currentWindow.localStorage.getItem(DIALER_UI_STORAGE_KEY) ??
        currentWindow.localStorage.getItem(LEGACY_WIDGET_LAYOUT_STORAGE_KEY);
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as DialerUiStoragePreferences | Record<string, string[]>;
          localPreference = parseDialerUiPreferences(parsed);
        } catch {
          localPreference = null;
        }
      }
    }

    const selectedPreference = localPreference ?? serverPreference;

    if (selectedPreference) {
      nextWidgetLayout = selectedPreference.widgetLayout;
      nextBelowMessagesLayout = selectedPreference.belowMessagesLayout;
      nextMessagesWidgetMode = selectedPreference.messagesWidgetMode;
      nextModeLayouts = selectedPreference.modeLayouts;
      nextWidgetDragLocked = selectedPreference.widgetDragLocked;
      nextCollapsedSections = selectedPreference.collapsedSections;
      nextHiddenSections = selectedPreference.hiddenSections;
    }

    const nextModeSnapshot = nextModeLayouts[nextMessagesWidgetMode];
    if (nextModeSnapshot) {
      nextWidgetLayout = nextModeSnapshot.widgetLayout;
      nextBelowMessagesLayout = nextModeSnapshot.belowMessagesLayout;
    } else {
      const rebalanced = rebalanceClassicTwoColumnsLayout(
        nextWidgetLayout,
        nextBelowMessagesLayout,
        toHybridWidgetMode(nextMessagesWidgetMode),
      );
      const split = splitClassicTwoColumnsLayout(rebalanced);
      nextWidgetLayout = split.widgetLayout;
      nextBelowMessagesLayout = split.belowMessagesLayout;
    }
    nextModeLayouts = {
      ...nextModeLayouts,
      [nextMessagesWidgetMode]: createModeLayoutSnapshot(nextWidgetLayout, nextBelowMessagesLayout),
    };

    setWidgetLayout(nextWidgetLayout);
    setBelowMessagesLayout(nextBelowMessagesLayout);
    messagesWidgetModeRef.current = nextMessagesWidgetMode;
    setMessagesWidgetMode(nextMessagesWidgetMode);
    modeLayoutsRef.current = nextModeLayouts;
    setModeLayouts(nextModeLayouts);
    widgetDragLockedRef.current = nextWidgetDragLocked;
    setIsWidgetDragLocked(nextWidgetDragLocked);
    setLeftPanelPercent(getAutoLeftPanelPercent(nextMessagesWidgetMode));
    setRightPanelPercent(DEFAULT_RIGHT_PANEL_PERCENT);
    collapsedSectionsRef.current = nextCollapsedSections;
    setCollapsedSections(nextCollapsedSections);
    hiddenSectionsRef.current = nextHiddenSections;
    setHiddenSections(nextHiddenSections);
    hasHydratedDialerPrefsRef.current = true;
    lastSavedDialerPrefsRef.current = JSON.stringify({
      widgetLayout: nextWidgetLayout,
      belowMessagesLayout: nextBelowMessagesLayout,
      messagesWidgetMode: nextMessagesWidgetMode,
      modeLayouts: nextModeLayouts,
      widgetDragLocked: nextWidgetDragLocked,
      collapsedSections: nextCollapsedSections,
      hiddenSections: nextHiddenSections,
    } satisfies DialerUiPreferences);
  }, [profilePreferencesQuery.data, profilePreferencesQuery.isPending]);

  useEffect(() => {
    widgetLayoutRef.current = widgetLayout;
  }, [widgetLayout]);

  useEffect(() => {
    belowMessagesLayoutRef.current = belowMessagesLayout;
  }, [belowMessagesLayout]);

  useEffect(() => {
    modeLayoutsRef.current = modeLayouts;
  }, [modeLayouts]);

  useEffect(() => {
    messagesWidgetModeRef.current = messagesWidgetMode;
  }, [messagesWidgetMode]);

  useEffect(() => {
    widgetDragLockedRef.current = isWidgetDragLocked;
  }, [isWidgetDragLocked]);

  useEffect(() => {
    collapsedSectionsRef.current = collapsedSections;
  }, [collapsedSections]);

  useEffect(() => {
    hiddenSectionsRef.current = hiddenSections;
  }, [hiddenSections]);

  useEffect(() => {
    const currentWindow = getBrowserWindow();
    if (!hasHydratedDialerPrefsRef.current || !currentWindow) return;
    const localPayload: DialerUiStoragePreferences = {
      widgetLayout,
      belowMessagesLayout,
      messagesWidgetMode,
      modeLayouts,
      widgetDragLocked: isWidgetDragLocked,
      collapsedSections,
      hiddenSections,
      localUpdatedAtMs: Date.now(),
    };
    currentWindow.localStorage.setItem(DIALER_UI_STORAGE_KEY, JSON.stringify(localPayload));
  }, [
    belowMessagesLayout,
    collapsedSections,
    hiddenSections,
    isWidgetDragLocked,
    modeLayouts,
    messagesWidgetMode,
    widgetLayout,
  ]);

  useEffect(() => {
    if (!hasHydratedDialerPrefsRef.current) return;

    const payload: DialerUiPreferences = {
      widgetLayout,
      belowMessagesLayout,
      messagesWidgetMode,
      modeLayouts,
      widgetDragLocked: isWidgetDragLocked,
      collapsedSections,
      hiddenSections,
    };
    const serialized = JSON.stringify(payload);
    if (serialized === lastSavedDialerPrefsRef.current) return;

    pendingDialerPrefsRef.current = {
      payload,
      serialized,
    };

    const currentWindow = getBrowserWindow();
    if (pendingDialerPrefsTimerRef.current !== null && currentWindow) {
      currentWindow.clearTimeout(pendingDialerPrefsTimerRef.current);
    }

    if (!currentWindow) {
      flushPendingDialerPreferenceSave();
      return;
    }

    pendingDialerPrefsTimerRef.current = currentWindow.setTimeout(() => {
      pendingDialerPrefsTimerRef.current = null;
      flushPendingDialerPreferenceSave();
    }, 350);
  }, [
    belowMessagesLayout,
    collapsedSections,
    flushPendingDialerPreferenceSave,
    hiddenSections,
    isWidgetDragLocked,
    modeLayouts,
    messagesWidgetMode,
    widgetLayout,
  ]);

  useEffect(() => {
    const currentWindow = getBrowserWindow();
    if (!currentWindow) return;

    const flushBeforeExit = () => {
      const localPayload: DialerUiStoragePreferences = {
        widgetLayout: widgetLayoutRef.current,
        belowMessagesLayout: belowMessagesLayoutRef.current,
        messagesWidgetMode: messagesWidgetModeRef.current,
        modeLayouts: modeLayoutsRef.current,
        widgetDragLocked: widgetDragLockedRef.current,
        collapsedSections: collapsedSectionsRef.current,
        hiddenSections: hiddenSectionsRef.current,
        localUpdatedAtMs: Date.now(),
      };
      currentWindow.localStorage.setItem(DIALER_UI_STORAGE_KEY, JSON.stringify(localPayload));
      flushPendingDialerPreferenceSave(true);
    };

    currentWindow.addEventListener('pagehide', flushBeforeExit);
    currentWindow.addEventListener('beforeunload', flushBeforeExit);
    return () => {
      currentWindow.removeEventListener('pagehide', flushBeforeExit);
      currentWindow.removeEventListener('beforeunload', flushBeforeExit);
      if (pendingDialerPrefsRetryTimerRef.current !== null) {
        currentWindow.clearTimeout(pendingDialerPrefsRetryTimerRef.current);
        pendingDialerPrefsRetryTimerRef.current = null;
      }
      flushBeforeExit();
    };
  }, [flushPendingDialerPreferenceSave]);

  const sessionQuery = useQuery({
    queryKey: ['dialer-session-lookup', dialTarget],
    enabled: canResolveSession,
    queryFn: async () => {
      const res = await api.lookupConversationByPhone(dialTarget);
      return res.data;
    },
    staleTime: 2_000,
    refetchInterval: livePollingProfile.sessionLookupMs,
  });

  const conversationsQuery = useQuery({
    queryKey: ['conversations', 'dialer-seed', seededConversationId],
    enabled: !dialTarget && !!seededConversationId,
    queryFn: async () => {
      const res = await api.getConversations();
      return res.data;
    },
    staleTime: 10_000,
    refetchInterval: false,
  });

  const allContactsQuery = useQuery({
    queryKey: ['dialer-contacts-seed', seededContactId],
    enabled: !dialTarget && !!seededContactId,
    queryFn: async () => {
      const res = await api.getContacts();
      return res.data;
    },
    staleTime: 10_000,
  });

  const contactsQuery = useQuery({
    queryKey: ['dialer-contacts-autocomplete', dialTarget],
    enabled: isPhoneInputFocused && dialTarget.length >= 2,
    queryFn: async () => {
      const res = await api.getContacts(dialTarget);
      return res.data;
    },
    staleTime: 10_000,
    refetchInterval: false,
  });

  const contactSuggestions = useMemo<ContactListItem[]>(
    () => (contactsQuery.data ?? []).slice(0, 6),
    [contactsQuery.data],
  );

  const activeConversation = sessionQuery.data?.conversation ?? null;
  const activeContact = sessionQuery.data?.contact ?? null;
  const activeConversationId = activeConversation?.id ?? null;
  const hasResolvedSession = Boolean(activeConversation || activeContact);
  const dialerRoutingReadiness = routingReadinessQuery.data ?? null;
  const manualVoiceAvailability = voiceAvailabilityQuery.data?.manualAvailability ?? false;
  const inboundAvailabilityEnabled = manualVoiceAvailability;
  const inboundAvailabilityLabel = inboundAvailabilityEnabled
    ? 'Inbound availability on'
    : 'Inbound availability off';
  const inboundEligibilityLabel = !inboundAvailabilityEnabled
    ? 'Inbound calls paused'
    : isRegistered
      ? 'Eligible for inbound calls'
      : 'Waiting for voice device registration';

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    liveFanoutVoiceStateRef.current = {
      callStatus,
      hasActiveCall,
    };
  }, [callStatus, hasActiveCall]);

  useEffect(() => {
    recentRealtimeTailRef.current = hasRecentRealtimeTail;
  }, [hasRecentRealtimeTail]);

  useEffect(() => {
    const pendingAppliedSuggestion = pendingAppliedSuggestionRef.current;
    if (!pendingAppliedSuggestion) return;
    if (!activeConversationId || pendingAppliedSuggestion.conversationId !== activeConversationId) {
      pendingAppliedSuggestionRef.current = null;
    }
  }, [activeConversationId]);

  const invalidateActiveConversationTransportQueries = useCallback(
    (conversationId: string) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dialer-events', conversationId] }),
        queryClient.invalidateQueries({ queryKey: ['dialer-calls', conversationId] }),
        queryClient.invalidateQueries({ queryKey: ['dialer-messages', conversationId] }),
        queryClient.invalidateQueries({ queryKey: ['dialer-feedback', conversationId] }),
      ]),
    [queryClient],
  );

  const recoverLiveTransportQueries = useCallback(() => {
    const conversationId = activeConversationIdRef.current;
    if (!conversationId) return;

    const realtimeNow =
      callStatus === 'calling' ||
      callStatus === 'ringing' ||
      (callStatus === 'in-progress' && hasActiveCall);
    const recentRealtimeTail = recentRealtimeTailRef.current;
    if (!realtimeNow && !recentRealtimeTail) return;

    eventsPollsSinceFullSyncRef.current = Number.MAX_SAFE_INTEGER;
    deltaEmptyPollsRef.current = 0;
    lastKnownEventCursorRef.current = null;

    void invalidateActiveConversationTransportQueries(conversationId);
  }, [callStatus, hasActiveCall, invalidateActiveConversationTransportQueries]);

  const handleLiveFanoutHint = useCallback(
    (hint: {
      kind: 'events' | 'calls';
      conversationId: string;
      latestCreatedAt?: string | null;
      latestCursor?: string | null;
    }) => {
      const conversationId = activeConversationIdRef.current;
      if (!conversationId || hint.conversationId !== conversationId) return;

      if (hint.kind === 'events') {
        const hintedCursor = hint.latestCursor ?? hint.latestCreatedAt ?? null;
        const knownCursor = lastKnownEventCursorRef.current;
        if (hintedCursor && knownCursor && hintedCursor <= knownCursor) {
          return;
        }
        deltaEmptyPollsRef.current = 0;
        void queryClient.invalidateQueries({ queryKey: ['dialer-events', conversationId] });
        return;
      }

      void queryClient.invalidateQueries({ queryKey: ['dialer-calls', conversationId] });
    },
    [queryClient],
  );

  useEffect(() => {
    eventsPollsSinceFullSyncRef.current = 0;
    realtimeTailUntilRef.current = 0;
    deltaEmptyPollsRef.current = 0;
    lastKnownEventCursorRef.current = null;
    setLiveFanoutState('closed');
    setHasRecentRealtimeTail(false);
  }, [activeConversationId]);

  useEffect(() => {
    if (!activeConversationId) {
      setHasRecentRealtimeTail(false);
      return;
    }

    const realtimeNow =
      callStatus === 'calling' ||
      callStatus === 'ringing' ||
      (callStatus === 'in-progress' && hasActiveCall);
    if (realtimeNow) {
      setHasRecentRealtimeTail(true);
      return;
    }

    const remainingTailMs = realtimeTailUntilRef.current - Date.now();
    if (remainingTailMs <= 0) {
      setHasRecentRealtimeTail(false);
      return;
    }

    const currentWindow = getBrowserWindow();
    if (!currentWindow) return;

    setHasRecentRealtimeTail(true);
    const timeout = currentWindow.setTimeout(() => {
      setHasRecentRealtimeTail(false);
    }, remainingTailMs);

    return () => {
      currentWindow.clearTimeout(timeout);
    };
  }, [activeConversationId, callStatus, hasActiveCall]);

  useEffect(() => {
    const previousStatus = previousVoiceStatusForRefreshRef.current;
    previousVoiceStatusForRefreshRef.current = callStatus;

    if (previousStatus === callStatus) return;
    if (!activeConversationId) return;

    const isRealtimeStatus = (status: VoiceCallStatus) =>
      status === 'calling' || status === 'ringing' || status === 'in-progress';
    if (!isRealtimeStatus(previousStatus) && !isRealtimeStatus(callStatus)) return;

    void invalidateActiveConversationTransportQueries(activeConversationId);
  }, [activeConversationId, callStatus, invalidateActiveConversationTransportQueries]);

  useEffect(() => {
    const currentDocument = getBrowserDocument();
    const currentWindow = getBrowserWindow();
    if (!currentDocument || !currentWindow) return;

    const handleVisibilityChange = () => {
      if (currentDocument.visibilityState !== 'visible') return;
      recoverLiveTransportQueries();
    };
    const handleOnline = () => {
      recoverLiveTransportQueries();
    };

    currentDocument.addEventListener('visibilitychange', handleVisibilityChange);
    currentWindow.addEventListener('online', handleOnline);

    return () => {
      currentDocument.removeEventListener('visibilitychange', handleVisibilityChange);
      currentWindow.removeEventListener('online', handleOnline);
    };
  }, [recoverLiveTransportQueries]);

  useEffect(() => {
    if (!activeConversationId) {
      setLiveFanoutState('closed');
      return;
    }

    const realtimeNow =
      callStatus === 'calling' ||
      callStatus === 'ringing' ||
      (callStatus === 'in-progress' && hasActiveCall);
    const recentRealtimeTail = hasRecentRealtimeTail;
    if (!realtimeNow && !recentRealtimeTail) {
      setLiveFanoutState('closed');
      return;
    }

    const session = startDialerLiveFanoutSession({
      conversationId: activeConversationId,
      requestTicket: async (conversationId) => {
        const res = await api.getConversationLiveTicket(conversationId);
        return res.data;
      },
      onStateChange: setLiveFanoutState,
      shouldStayConnected: () => {
        if (activeConversationIdRef.current !== activeConversationId) return false;

        const liveVoiceState = liveFanoutVoiceStateRef.current;
        return (
          liveVoiceState.callStatus === 'calling' ||
          liveVoiceState.callStatus === 'ringing' ||
          (liveVoiceState.callStatus === 'in-progress' && liveVoiceState.hasActiveCall) ||
          recentRealtimeTailRef.current
        );
      },
      onHint: handleLiveFanoutHint,
      hintCoalesceWindowMs: 150,
    });

    return () => {
      session.close();
    };
  }, [
    activeConversationId,
    callStatus,
    handleLiveFanoutHint,
    hasActiveCall,
    hasRecentRealtimeTail,
  ]);

  useEffect(() => {
    dialTargetRef.current = dialTarget;
  }, [dialTarget]);

  useEffect(() => {
    if (dialTarget || !seededConversationId || !conversationsQuery.data) return;
    const seededConversation = conversationsQuery.data.find(
      (conversation) => conversation.id === seededConversationId,
    );
    if (seededConversation) {
      setPhoneNumber(seededConversation.contactPhone, true);
    }
  }, [dialTarget, seededConversationId, conversationsQuery.data, setPhoneNumber]);

  useEffect(() => {
    if (dialTarget || !seededContactId || !allContactsQuery.data) return;
    const seededContact = allContactsQuery.data.find((contact) => contact.id === seededContactId);
    if (seededContact) {
      setPhoneNumber(seededContact.phoneNumber, true);
    }
  }, [allContactsQuery.data, dialTarget, seededContactId, setPhoneNumber]);

  useEffect(() => {
    if (!activeContact) {
      setIsEditingContact(false);
      setEditContactName('');
      setEditContactPhone('');
      setEditContactEmail('');
      return;
    }

    setEditContactName(activeContact.displayName ?? '');
    setEditContactPhone(activeContact.phoneNumber);
    setEditContactEmail(activeContact.email ?? '');
  }, [activeContact]);

  const messagesQuery = useQuery({
    queryKey: ['dialer-messages', activeConversationId],
    enabled: !!activeConversationId,
    queryFn: async () => {
      const res = await api.getMessages(activeConversationId!);
      // API returns desc; feed should render chronological.
      return res.data.reverse();
    },
    refetchInterval: livePollingProfile.messagesMs,
  });

  const eventsQuery = useQuery({
    queryKey: ['dialer-events', activeConversationId],
    enabled: !!activeConversationId,
    queryFn: async () => {
      const eventsQueryKey = ['dialer-events', activeConversationId];
      const existing = queryClient.getQueryData<ConversationEvent[]>(eventsQueryKey) ?? [];
      const nowMs = Date.now();
      const realtimeNow =
        callStatus === 'calling' ||
        callStatus === 'ringing' ||
        (callStatus === 'in-progress' && hasActiveCall);
      if (realtimeNow) {
        // Keep a brief post-call tail so AI summary/highlights are pulled quickly after call end.
        realtimeTailUntilRef.current = nowMs + 30_000;
      }

      const cursor = lastKnownEventCursorRef.current ?? existing[0]?.createdAt ?? null;
      const recentRealtimeTail = nowMs <= realtimeTailUntilRef.current;
      const shouldForceFullFromEmptyDeltas = deltaEmptyPollsRef.current >= 6;
      const plan = resolveDialerEventFetchPlan({
        callStatus,
        hasActiveCall,
        latestKnownEventAt: cursor,
        pollsSinceFullSync: shouldForceFullFromEmptyDeltas
          ? Number.MAX_SAFE_INTEGER
          : eventsPollsSinceFullSyncRef.current,
        recentRealtimeTail,
      });

      if (plan.mode === 'delta') {
        eventsPollsSinceFullSyncRef.current += 1;
        try {
          const res = await api.getConversationEvents(activeConversationId!, {
            after: plan.after,
            types: plan.types,
            limit: plan.limit,
          });
          const merged = mergeConversationEventTimeline(existing, res.data);
          lastKnownEventCursorRef.current =
            res.meta?.cursors.latest ?? merged[0]?.createdAt ?? null;
          if (res.data.length === 0) {
            deltaEmptyPollsRef.current += 1;
          } else {
            deltaEmptyPollsRef.current = 0;
          }
          return merged;
        } catch {
          eventsPollsSinceFullSyncRef.current = 0;
          deltaEmptyPollsRef.current = 0;
          const full = await api.getConversationEvents(activeConversationId!);
          lastKnownEventCursorRef.current =
            full.meta?.cursors.latest ?? full.data[0]?.createdAt ?? null;
          return full.data;
        }
      }

      eventsPollsSinceFullSyncRef.current = 0;
      deltaEmptyPollsRef.current = 0;
      const res = await api.getConversationEvents(activeConversationId!);
      lastKnownEventCursorRef.current = res.meta?.cursors.latest ?? res.data[0]?.createdAt ?? null;
      return res.data;
    },
    refetchInterval: livePollingProfile.eventsMs,
  });

  const callsQuery = useQuery({
    queryKey: ['dialer-calls', activeConversationId],
    enabled: !!activeConversationId,
    queryFn: async () => {
      const res = await api.getConversationCalls(activeConversationId!);
      return res.data;
    },
    refetchInterval: livePollingProfile.callsMs,
  });

  const feedbackQuery = useQuery({
    queryKey: ['dialer-feedback', activeConversationId],
    enabled: !!activeConversationId,
    queryFn: async () => {
      const res = await api.getConversationAiFeedback(activeConversationId!);
      return res.data;
    },
    refetchInterval: livePollingProfile.feedbackMs,
  });

  const ragSuggestionsQuery = useQuery({
    queryKey: ['dialer-rag-suggestions', activeConversationId],
    enabled: !!activeConversationId,
    queryFn: async () => {
      const res = await api.getRagSuggestions(activeConversationId!);
      return res.data;
    },
    refetchInterval: livePollingProfile.suggestionsMs,
  });

  const sendMutation = useMutation({
    mutationFn: async (text: string) => {
      if (!canResolveSession) {
        throw new Error('Enter a valid phone number before sending a message');
      }

      if (activeConversationId) {
        await api.sendMessage(activeConversationId, text);
        return {
          conversationId: activeConversationId,
          text,
        };
      }

      const created = await api.sendSms(dialTarget, text);
      return {
        conversationId: created.conversationId,
        text,
      };
    },
    onSuccess: async ({ conversationId, text }) => {
      const pendingAppliedSuggestion = pendingAppliedSuggestionRef.current;
      setSmsBody('');
      pendingAppliedSuggestionRef.current = null;

      if (pendingAppliedSuggestion && pendingAppliedSuggestion.conversationId === conversationId) {
        try {
          await api.submitRagFeedback({
            suggestionId: pendingAppliedSuggestion.suggestionId,
            signal: 'accept',
            editedText: text,
          });
          await queryClient.invalidateQueries({
            queryKey: ['dialer-rag-suggestions', conversationId],
          });
        } catch (error) {
          console.error('Failed to persist applied RAG suggestion feedback:', error);
        }
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dialer-messages', conversationId] }),
        queryClient.invalidateQueries({ queryKey: ['dialer-events', conversationId] }),
        queryClient.invalidateQueries({ queryKey: ['dialer-session-lookup', dialTarget] }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['conversations'] }),
      ]);
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: async () => {
      if (!activeContact) throw new Error('No active contact');

      return api.updateContact(activeContact.id, {
        displayName: editContactName.trim() || undefined,
        phoneNumber: editContactPhone.trim(),
        email: editContactEmail.trim() || undefined,
      });
    },
    onSuccess: async ({ data }) => {
      setIsEditingContact(false);
      setPhoneNumber(data.phoneNumber, false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dialer-session-lookup'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['conversations'] }),
      ]);
    },
  });

  const regenerateInsightsMutation = useMutation({
    mutationFn: async () => {
      if (!activeConversationIdRef.current) {
        throw new Error('No active conversation selected');
      }
      return api.regenerateConversationInsights(activeConversationIdRef.current);
    },
    onSuccess: async () => {
      if (!activeConversationIdRef.current) return;
      await queryClient.invalidateQueries({
        queryKey: ['dialer-events', activeConversationIdRef.current],
      });
    },
  });

  const submitFeedbackMutation = useMutation({
    mutationFn: async (payload: {
      targetType: AiFeedbackTargetType;
      signal: AiFeedbackSignal;
      targetEventId?: string;
      note?: string;
    }) => {
      if (!activeConversationIdRef.current) {
        throw new Error('No active conversation selected');
      }
      return api.submitConversationAiFeedback(activeConversationIdRef.current, payload);
    },
    onSuccess: async () => {
      if (!activeConversationIdRef.current) return;
      await queryClient.invalidateQueries({
        queryKey: ['dialer-feedback', activeConversationIdRef.current],
      });
    },
  });

  const generateRagSuggestionsMutation = useMutation({
    mutationFn: async () => {
      if (!activeConversationIdRef.current) {
        throw new Error('No active conversation selected');
      }

      return api.generateRagSuggestions({
        conversationId: activeConversationIdRef.current,
        prompt: ragPrompt.trim() || undefined,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['dialer-rag-suggestions', activeConversationIdRef.current],
      });
    },
  });

  const submitRagSuggestionFeedbackMutation = useMutation({
    mutationFn: async (payload: { suggestionId: string; signal: 'accept' | 'reject' }) =>
      api.submitRagFeedback(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['dialer-rag-suggestions', activeConversationIdRef.current],
      });
    },
  });

  const logVoiceEvent = useCallback(
    async (
      eventType: ConversationEventType,
      body?: string,
      conversationIdOverride?: string | null,
    ) => {
      const targetConversationId = conversationIdOverride ?? activeConversationIdRef.current;
      if (!targetConversationId) return;

      await api.createConversationEvent(targetConversationId, {
        channel: 'voice',
        eventType,
        body,
      });

      await queryClient.invalidateQueries({ queryKey: ['dialer-events', targetConversationId] });
    },
    [queryClient],
  );

  const messages = useMemo(() => messagesQuery.data ?? [], [messagesQuery.data]);
  const events = useMemo(() => eventsQuery.data ?? [], [eventsQuery.data]);
  const callHistory = useMemo<CallRecord[]>(() => callsQuery.data ?? [], [callsQuery.data]);
  const ragSuggestions = useMemo<RagSuggestion[]>(
    () => ragSuggestionsQuery.data ?? [],
    [ragSuggestionsQuery.data],
  );

  const transcriptEvents = useMemo(
    () => events.filter((event) => event.eventType === 'voice_transcript'),
    [events],
  );
  const latestTranscriptAt = useMemo(() => {
    if (transcriptEvents.length === 0) return null;
    let latest: string | null = null;
    let latestMs = -Infinity;
    for (const event of transcriptEvents) {
      const eventMs = Date.parse(event.createdAt);
      if (Number.isNaN(eventMs)) continue;
      if (eventMs > latestMs) {
        latestMs = eventMs;
        latest = event.createdAt;
      }
    }
    return latest;
  }, [transcriptEvents]);
  const transcriptLiveHealth = useMemo(
    () =>
      resolveTranscriptLiveHealth({
        callStatus,
        hasActiveCall,
        latestTranscriptAt,
      }),
    [callStatus, hasActiveCall, latestTranscriptAt],
  );
  const transcriptTransportStatus = useMemo(
    () =>
      resolveDialerLiveTransportStatus({
        callStatus,
        hasActiveCall,
        recentRealtimeTail: hasRecentRealtimeTail,
        liveFanoutState,
      }),
    [callStatus, hasActiveCall, hasRecentRealtimeTail, liveFanoutState],
  );

  const actionItemEvents = useMemo(
    () => events.filter((event) => event.eventType === 'action_item'),
    [events],
  );

  const summaryEvents = useMemo(
    () => events.filter((event) => event.eventType === 'summary'),
    [events],
  );

  const highlightEvents = useMemo(
    () => events.filter((event) => event.eventType === 'highlight'),
    [events],
  );

  const fallbackSummaryText = useMemo(() => buildFallbackSummary(events), [events]);
  const fallbackHighlights = useMemo(() => pickFallbackHighlights(events), [events]);
  const summaryText = summaryEvents[0]?.body || fallbackSummaryText;
  const highlights = useMemo(
    () =>
      highlightEvents.length > 0
        ? highlightEvents
            .map((event) => event.body)
            .filter((value): value is string => Boolean(value))
        : fallbackHighlights,
    [highlightEvents, fallbackHighlights],
  );
  const feedbackEntries = useMemo<AiFeedbackEntry[]>(
    () => feedbackQuery.data ?? [],
    [feedbackQuery.data],
  );
  const latestFeedbackByTarget = useMemo(() => {
    const latest = new Map<string, AiFeedbackEntry>();
    for (const entry of feedbackEntries) {
      const key = buildFeedbackKey(entry.targetType, entry.targetEventId);
      if (!latest.has(key)) {
        latest.set(key, entry);
      }
    }
    return latest;
  }, [feedbackEntries]);
  const isFeedbackSubmitting = submitFeedbackMutation.isPending;

  const getFeedbackSignal = useCallback(
    (targetType: AiFeedbackTargetType, targetEventId?: string | null): AiFeedbackSignal | null => {
      const entry = latestFeedbackByTarget.get(buildFeedbackKey(targetType, targetEventId));
      return entry?.signal ?? null;
    },
    [latestFeedbackByTarget],
  );

  const handleFeedback = useCallback(
    (targetType: AiFeedbackTargetType, signal: AiFeedbackSignal, targetEventId?: string | null) => {
      const expectedType = INSIGHT_EVENT_TYPE_MAP[targetType];
      if (targetEventId) {
        const matching = events.find(
          (event) => event.id === targetEventId && event.eventType === expectedType,
        );
        if (!matching) return;
      }

      submitFeedbackMutation.mutate({
        targetType,
        signal,
        targetEventId: targetEventId ?? undefined,
      });
    },
    [events, submitFeedbackMutation],
  );

  const handleApplySuggestion = useCallback((suggestion: RagSuggestion) => {
    pendingAppliedSuggestionRef.current =
      activeConversationIdRef.current === suggestion.conversationId
        ? {
            suggestionId: suggestion.id,
            conversationId: suggestion.conversationId,
          }
        : null;
    setSmsBody(suggestion.suggestionText);
  }, []);

  const handleRagSuggestionFeedback = useCallback(
    (payload: { suggestionId: string; signal: 'accept' | 'reject' }) => {
      submitRagSuggestionFeedbackMutation.mutate(payload);
    },
    [submitRagSuggestionFeedbackMutation],
  );

  const applyClassicTwoColumnsLayout = useCallback(
    (
      nextLayout: ReturnType<typeof normalizeClassicTwoColumnsLayout>,
      mode: DialerWidgetViewMode = messagesWidgetMode,
    ) => {
      const split = splitClassicTwoColumnsLayout(nextLayout);
      const snapshot = createModeLayoutSnapshot(split.widgetLayout, split.belowMessagesLayout);
      widgetLayoutRef.current = split.widgetLayout;
      belowMessagesLayoutRef.current = split.belowMessagesLayout;
      setWidgetLayout(split.widgetLayout);
      setBelowMessagesLayout(split.belowMessagesLayout);
      setModeLayouts((current) => {
        const next = {
          ...current,
          [mode]: snapshot,
        };
        modeLayoutsRef.current = next;
        return next;
      });
    },
    [messagesWidgetMode],
  );

  const handleMessagesWidgetModeChange = useCallback(
    (nextMode: DialerWidgetViewMode) => {
      if (nextMode === messagesWidgetMode) return;

      const currentMode = messagesWidgetMode;
      const currentModeSnapshot = createModeLayoutSnapshot(
        widgetLayoutRef.current,
        belowMessagesLayoutRef.current,
      );
      const savedNextModeSnapshot = modeLayoutsRef.current[nextMode];
      const nextLayout = savedNextModeSnapshot
        ? normalizeClassicTwoColumnsLayout(
            savedNextModeSnapshot.widgetLayout,
            savedNextModeSnapshot.belowMessagesLayout,
          )
        : rebalanceClassicTwoColumnsLayout(
            widgetLayoutRef.current,
            belowMessagesLayoutRef.current,
            toHybridWidgetMode(nextMode),
          );

      setModeLayouts((current) => {
        const next = {
          ...current,
          [currentMode]: currentModeSnapshot,
        };
        modeLayoutsRef.current = next;
        return next;
      });
      messagesWidgetModeRef.current = nextMode;
      setMessagesWidgetMode(nextMode);
      setLeftPanelPercent(getAutoLeftPanelPercent(nextMode));
      setRightPanelPercent(DEFAULT_RIGHT_PANEL_PERCENT);
      applyClassicTwoColumnsLayout(nextLayout, nextMode);
    },
    [applyClassicTwoColumnsLayout, messagesWidgetMode],
  );

  useEffect(() => {
    const currentWindow = getBrowserWindow();
    if (!currentWindow) return;

    const handleModeSwitch = (event: Event) => {
      const detail = (event as CustomEvent<{ mode?: unknown }>).detail;
      const nextMode = normalizeDialerWidgetMode(detail?.mode);
      handleMessagesWidgetModeChange(nextMode);
    };
    const handleDragLockSwitch = (event: Event) => {
      const detail = (event as CustomEvent<{ locked?: unknown }>).detail;
      const locked = detail?.locked === true;
      widgetDragLockedRef.current = locked;
      setIsWidgetDragLocked(locked);
    };

    currentWindow.addEventListener(DIALER_WIDGET_MODE_EVENT, handleModeSwitch as EventListener);
    currentWindow.addEventListener(
      DIALER_WIDGET_DRAG_LOCK_EVENT,
      handleDragLockSwitch as EventListener,
    );
    return () => {
      currentWindow.removeEventListener(
        DIALER_WIDGET_MODE_EVENT,
        handleModeSwitch as EventListener,
      );
      currentWindow.removeEventListener(
        DIALER_WIDGET_DRAG_LOCK_EVENT,
        handleDragLockSwitch as EventListener,
      );
    };
  }, [handleMessagesWidgetModeChange]);

  const handleWidgetDragStart = useCallback(
    (activeId: string) => {
      if (widgetDragLockedRef.current) return;
      const sourceContainer = resolveClassicTwoColumnsContainer(
        normalizeClassicTwoColumnsLayout(widgetLayoutRef.current, belowMessagesLayoutRef.current),
        activeId,
        toHybridWidgetMode(messagesWidgetMode),
      );
      if (!sourceContainer) return;
      setActiveDraggedWidget(activeId as DialerWidgetId);
    },
    [messagesWidgetMode],
  );

  const handleWidgetDragOver = useCallback(
    (activeId: string, overId: string | null) => {
      if (widgetDragLockedRef.current) return;
      if (!overId) return;

      const normalizedLayout = normalizeClassicTwoColumnsLayout(
        widgetLayoutRef.current,
        belowMessagesLayoutRef.current,
      );
      const nextLayout = moveClassicTwoColumnsWidgetInLayout(
        normalizedLayout,
        activeId as DialerWidgetId,
        overId,
        toHybridWidgetMode(messagesWidgetMode),
      );
      if (nextLayout === normalizedLayout) return;
      applyClassicTwoColumnsLayout(nextLayout);
    },
    [applyClassicTwoColumnsLayout, messagesWidgetMode],
  );

  const handleWidgetDragEnd = useCallback(
    (activeId: string, overId: string | null) => {
      if (widgetDragLockedRef.current) {
        setActiveDraggedWidget(null);
        return;
      }
      if (overId) {
        const normalizedLayout = normalizeClassicTwoColumnsLayout(
          widgetLayoutRef.current,
          belowMessagesLayoutRef.current,
        );
        const nextLayout = moveClassicTwoColumnsWidgetInLayout(
          normalizedLayout,
          activeId as DialerWidgetId,
          overId,
          toHybridWidgetMode(messagesWidgetMode),
        );
        if (nextLayout !== normalizedLayout) {
          applyClassicTwoColumnsLayout(nextLayout);
        }
      }
      setActiveDraggedWidget(null);
    },
    [applyClassicTwoColumnsLayout, messagesWidgetMode],
  );

  useEffect(() => {
    if (!isWidgetDragLocked) return;
    setActiveDraggedWidget(null);
  }, [isWidgetDragLocked]);

  // Auto-scroll feed to bottom when new messages arrive.
  useEffect(() => {
    feedEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const currentWindow = getBrowserWindow();
    if (!currentWindow) return;

    const handleViewportResize = () => {
      setIsDesktopLayout(currentWindow.innerWidth >= 1024);
    };

    handleViewportResize();
    currentWindow.addEventListener('resize', handleViewportResize);
    return () => currentWindow.removeEventListener('resize', handleViewportResize);
  }, []);

  useEffect(() => {
    const currentWindow = getBrowserWindow();
    if (!currentWindow || typeof navigator === 'undefined') return;

    const bump = () => setNetworkProbeTick((current) => current + 1);
    const nav = navigator as Navigator & {
      connection?: EventTarget;
      mozConnection?: EventTarget;
      webkitConnection?: EventTarget;
    };
    const connection = nav.connection ?? nav.mozConnection ?? nav.webkitConnection ?? null;

    currentWindow.addEventListener('online', bump);
    currentWindow.addEventListener('offline', bump);
    connection?.addEventListener('change', bump);

    return () => {
      currentWindow.removeEventListener('online', bump);
      currentWindow.removeEventListener('offline', bump);
      connection?.removeEventListener('change', bump);
    };
  }, []);

  useEffect(() => {
    const currentWindow = getBrowserWindow();
    const currentDocument = getBrowserDocument();
    if (!currentWindow || !currentDocument || !isDesktopLayout || !splitResizeTarget) return;

    const handlePointerMove = (event: MouseEvent) => {
      if (!splitContainerRef.current) return;
      const bounds = splitContainerRef.current.getBoundingClientRect();
      if (splitResizeTarget === 'left') {
        const maxLeftPanelPercent =
          messagesWidgetMode === 'three_columns'
            ? Math.min(
                getMaxLeftPanelPercent(messagesWidgetMode),
                100 - rightPanelPercent - MIN_CENTER_PANEL_PERCENT,
              )
            : getMaxLeftPanelPercent(messagesWidgetMode);
        setLeftPanelPercent(
          getSplitPercentFromPointer(
            event.clientX,
            { left: bounds.left, width: bounds.width },
            MIN_PARENT_COLUMN_PERCENT,
            maxLeftPanelPercent,
          ),
        );
        return;
      }

      if (messagesWidgetMode !== 'three_columns') return;

      const boundaryPercent = getSplitPercentFromPointer(
        event.clientX,
        { left: bounds.left, width: bounds.width },
        leftPanelPercent + MIN_CENTER_PANEL_PERCENT,
        100 - MIN_PARENT_COLUMN_PERCENT,
      );
      setRightPanelPercent(
        clampSplitPercent(
          100 - boundaryPercent,
          MIN_PARENT_COLUMN_PERCENT,
          getMaxRightPanelPercent(leftPanelPercent),
        ),
      );
    };

    const handlePointerUp = () => {
      setSplitResizeTarget(null);
    };

    currentWindow.addEventListener('mousemove', handlePointerMove);
    currentWindow.addEventListener('mouseup', handlePointerUp);
    currentDocument.body.style.cursor = 'col-resize';
    currentDocument.body.style.userSelect = 'none';

    return () => {
      currentWindow.removeEventListener('mousemove', handlePointerMove);
      currentWindow.removeEventListener('mouseup', handlePointerUp);
      currentDocument.body.style.cursor = '';
      currentDocument.body.style.userSelect = '';
    };
  }, [isDesktopLayout, leftPanelPercent, messagesWidgetMode, rightPanelPercent, splitResizeTarget]);

  useEffect(() => {
    const maxLeftPanelPercent =
      messagesWidgetMode === 'three_columns'
        ? Math.min(
            getMaxLeftPanelPercent(messagesWidgetMode),
            100 - rightPanelPercent - MIN_CENTER_PANEL_PERCENT,
          )
        : getMaxLeftPanelPercent(messagesWidgetMode);
    setLeftPanelPercent((current) =>
      clampSplitPercent(current, MIN_PARENT_COLUMN_PERCENT, maxLeftPanelPercent),
    );
  }, [messagesWidgetMode, rightPanelPercent]);

  useEffect(() => {
    if (messagesWidgetMode !== 'three_columns') return;
    setRightPanelPercent((current) =>
      clampSplitPercent(
        current,
        MIN_PARENT_COLUMN_PERCENT,
        getMaxRightPanelPercent(leftPanelPercent),
      ),
    );
  }, [leftPanelPercent, messagesWidgetMode]);

  useEffect(() => subscribeTwilioVoice(setVoiceState), []);

  useEffect(() => {
    void ensureTwilioVoiceDevice();
  }, []);

  useEffect(() => {
    const currentWindow = getBrowserWindow();
    const currentDocument = getBrowserDocument();
    if (!currentWindow || !currentDocument || typeof Notification === 'undefined') return;

    if (!isInboundRinging) {
      inboundBrowserNotificationRef.current?.close();
      inboundBrowserNotificationRef.current = null;
      return;
    }

    if (currentDocument.visibilityState === 'visible') return;

    if (Notification.permission === 'default') {
      void Notification.requestPermission().catch(() => undefined);
      return;
    }

    if (Notification.permission !== 'granted') return;
    if (inboundBrowserNotificationRef.current) return;

    const notification = new Notification('Incoming call', {
      body: 'CallHub dialer is ringing now.',
      tag: 'callhub-inbound-call',
      requireInteraction: true,
    });
    notification.onclick = () => {
      void currentWindow.focus();
      notification.close();
    };
    notification.onclose = () => {
      if (inboundBrowserNotificationRef.current === notification) {
        inboundBrowserNotificationRef.current = null;
      }
    };
    inboundBrowserNotificationRef.current = notification;
  }, [isInboundRinging]);

  useEffect(
    () => () => {
      inboundBrowserNotificationRef.current?.close();
      inboundBrowserNotificationRef.current = null;
    },
    [],
  );

  useEffect(() => {
    const previous = previousVoiceSnapshotRef.current;

    if (
      previous.direction === 'inbound' &&
      previous.status === 'ringing' &&
      callStatus === 'in-progress'
    ) {
      const inboundTarget = dialTargetRef.current || 'contact';
      void logVoiceEvent('call_started', `Inbound call active with ${inboundTarget}`);
    }

    if (
      previous.direction === 'inbound' &&
      previous.status === 'in-progress' &&
      callStatus === 'idle'
    ) {
      const inboundTarget = dialTargetRef.current || 'contact';
      void logVoiceEvent('call_ended', `Inbound call ended with ${inboundTarget}`);
    }

    previousVoiceSnapshotRef.current = {
      status: callStatus,
      direction: voiceState.activeCallDirection,
    };
  }, [callStatus, logVoiceEvent, voiceState.activeCallDirection]);

  const handleDigitPress = useCallback(
    (digit: string) => {
      if (activeCall && callStatus === 'in-progress') {
        sendDigitsOnActiveCall(digit);
      } else {
        setPhoneNumber(phoneNumber + digit);
      }
    },
    [activeCall, callStatus, phoneNumber, setPhoneNumber],
  );

  useEffect(() => {
    const currentWindow = getBrowserWindow();
    if (!currentWindow) return;

    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;

      if (event.key === 'Backspace') {
        if (callStatus === 'idle' && phoneNumber.length > 0) {
          event.preventDefault();
          setPhoneNumber(phoneNumber.slice(0, -1));
          phoneInputRef.current?.focus();
        }
        return;
      }

      const isDialpadSymbol = KEYPAD_KEYS.includes(event.key);
      const isPhonePrefixPlus = event.key === '+' && callStatus !== 'in-progress';
      if (isDialpadSymbol || isPhonePrefixPlus) {
        event.preventDefault();
        if (!(activeCall && callStatus === 'in-progress')) {
          phoneInputRef.current?.focus();
        }
        handleDigitPress(event.key);
      }
    };

    currentWindow.addEventListener('keydown', handleGlobalKeyDown);
    return () => currentWindow.removeEventListener('keydown', handleGlobalKeyDown);
  }, [activeCall, callStatus, handleDigitPress, phoneNumber, setPhoneNumber]);

  const handleCall = useCallback(async () => {
    setAutoCallError(null);

    if (activeCall) {
      if (callStatus !== 'ringing') {
        void logVoiceEvent('call_ended', `Call ended with ${dialTarget || phoneNumber}`);
      }
      acceptOrHangupCurrentCall();
      return;
    }

    if (!canResolveSession) return;

    const destination = dialTarget;

    try {
      // Ensure we always have a persisted contact/conversation once a call is placed.
      const session = await api.getConversationByPhone(destination);
      const conversationId = session.data.conversation.id;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dialer-session-lookup', destination] }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['conversations'] }),
      ]);

      const call = await startOutboundCall(destination);

      call.on('accept', () => {
        void logVoiceEvent(
          'call_started',
          `Outbound call connected with ${destination}`,
          conversationId,
        );
      });

      call.on('disconnect', () => {
        void logVoiceEvent('call_ended', `Outbound call ended with ${destination}`, conversationId);
      });

      call.on('error', (err) => {
        console.error('Call Error:', err);
      });
    } catch (err) {
      console.error('Failed to dial out:', err);
    }
  }, [
    activeCall,
    callStatus,
    canResolveSession,
    dialTarget,
    logVoiceEvent,
    phoneNumber,
    queryClient,
    setAutoCallError,
  ]);

  const handleSendSms = useCallback(() => {
    const trimmedBody = smsBody.trim();
    if (!trimmedBody || !canResolveSession) return;
    sendMutation.mutate(trimmedBody);
  }, [canResolveSession, sendMutation, smsBody]);

  const handleSelectContactSuggestion = useCallback(
    (contact: ContactListItem) => {
      setPhoneNumber(contact.phoneNumber);
      setIsPhoneInputFocused(false);
      requestAnimationFrame(() => {
        phoneInputRef.current?.focus();
      });
    },
    [setPhoneNumber],
  );

  const handleSaveContact = useCallback(() => {
    if (!activeContact || updateContactMutation.isPending) return;
    updateContactMutation.mutate();
  }, [activeContact, updateContactMutation]);

  const handleSplitResizeStart = useCallback(
    (target: Exclude<SplitResizeTarget, null>) => {
      if (!isDesktopLayout) return;
      if (target === 'right' && messagesWidgetMode !== 'three_columns') return;
      setSplitResizeTarget(target);
    },
    [isDesktopLayout, messagesWidgetMode],
  );

  const networkReadiness = useMemo(() => {
    void networkProbeTick;
    return buildNetworkReadinessState(isRegistered);
  }, [isRegistered, networkProbeTick]);
  const unmetRequiredChecks = networkReadiness.checks.filter(
    (check) => check.required && !check.ok,
  );
  const connectionProfileCheck = networkReadiness.checks.find(
    (check) => check.id === 'connection-profile',
  );
  const isOnCall = callStatus === 'in-progress' || callStatus === 'calling';
  const showCallQualityPanel = true;
  const callQualityHasLiveTelemetry =
    callStatus !== 'idle' || callQuality.updatedAt !== null || callQuality.warnings.length > 0;
  const showContactSuggestions =
    isPhoneInputFocused && dialTarget.length >= 2 && contactSuggestions.length > 0;
  const callBtnClass = isOnCall
    ? 'bg-red-600 hover:bg-red-700 shadow-red-600/35 dark:bg-red-500 dark:hover:bg-red-400'
    : callStatus === 'ringing'
      ? 'animate-pulse bg-amber-600 hover:bg-amber-700 shadow-amber-600/35 dark:bg-amber-500 dark:hover:bg-amber-400'
      : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/35 dark:bg-emerald-500 dark:hover:bg-emerald-400';
  const callIcon = isOnCall ? 'call_end' : callStatus === 'ringing' ? 'ring_volume' : 'call';
  const callButtonAriaLabel = isOnCall
    ? 'End call'
    : callStatus === 'ringing'
      ? 'Answer call'
      : 'Start call';

  useEffect(() => {
    const shouldAutoCall = dialerAction === 'call';
    if (!shouldAutoCall) {
      autoCallHandledRef.current = false;
      setAutoCallError(null);
      return;
    }
    if (autoCallHandledRef.current) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('action');

    if (!canResolveSession) {
      setAutoCallError('Auto-call needs a valid phone number. Dial target was prefilled instead.');
      autoCallHandledRef.current = true;
      if (nextSearchParams.toString() !== searchParams.toString()) {
        setSearchParams(nextSearchParams, { replace: true });
      }
      return;
    }

    if (!networkReadiness.ready) {
      setAutoCallError(
        'Auto-call paused: readiness checks failed. Resolve connection checks and call manually.',
      );
      autoCallHandledRef.current = true;
      if (nextSearchParams.toString() !== searchParams.toString()) {
        setSearchParams(nextSearchParams, { replace: true });
      }
      return;
    }

    autoCallHandledRef.current = true;
    setAutoCallError(null);
    void handleCall();
    if (nextSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [
    canResolveSession,
    dialerAction,
    handleCall,
    networkReadiness.ready,
    searchParams,
    setSearchParams,
  ]);

  const visibleClassicTwoColumnsLayout = useMemo<DialerClassicTwoColumnsLayout>(() => {
    const normalized = normalizeClassicTwoColumnsLayout(widgetLayout, belowMessagesLayout);
    return {
      left: normalized.left.filter(
        (widgetId) => !hiddenSections[WIDGET_TO_HIDEABLE_SECTION_KEY[widgetId]],
      ),
      below_left: normalized.below_left.filter(
        (widgetId) => !hiddenSections[WIDGET_TO_HIDEABLE_SECTION_KEY[widgetId]],
      ),
      below_middle: normalized.below_middle.filter(
        (widgetId) => !hiddenSections[WIDGET_TO_HIDEABLE_SECTION_KEY[widgetId]],
      ),
      below_right: normalized.below_right.filter(
        (widgetId) => !hiddenSections[WIDGET_TO_HIDEABLE_SECTION_KEY[widgetId]],
      ),
      right: normalized.right.filter(
        (widgetId) => !hiddenSections[WIDGET_TO_HIDEABLE_SECTION_KEY[widgetId]],
      ),
    };
  }, [belowMessagesLayout, hiddenSections, widgetLayout]);
  const showSummaryRefreshButton = useMemo(
    () =>
      shouldShowSummaryRefresh({
        hasActiveContact: Boolean(activeContact),
        hasActiveConversation: Boolean(activeConversationId),
        callLogCount: callHistory.length,
        conversationMessageCount: messages.length,
        conversationEventCount: events.length,
      }),
    [activeContact, activeConversationId, callHistory.length, events.length, messages.length],
  );
  const isRagFeedbackPending = submitRagSuggestionFeedbackMutation.isPending;
  const hiddenSectionsCount = useMemo(
    () => PAGE_PREFERENCES_ORDER.filter((section) => hiddenSections[section]).length,
    [hiddenSections],
  );
  const leftSectionItems = visibleClassicTwoColumnsLayout.left;
  const rightSectionItems = visibleClassicTwoColumnsLayout.right;
  const showRightParentColumn = messagesWidgetMode === 'three_columns';
  const centerPanelPercent = showRightParentColumn
    ? Math.max(MIN_CENTER_PANEL_PERCENT, 100 - leftPanelPercent - rightPanelPercent)
    : Math.max(MIN_CENTER_PANEL_PERCENT, 100 - leftPanelPercent);
  const leftPanelStyle = isDesktopLayout ? { width: `${leftPanelPercent}%` } : undefined;
  const centerPanelStyle = isDesktopLayout ? { width: `${centerPanelPercent}%` } : undefined;
  const rightPanelStyle = isDesktopLayout ? { width: `${rightPanelPercent}%` } : undefined;
  const isResizingLeftSplit = splitResizeTarget === 'left';
  const isResizingRightSplit = splitResizeTarget === 'right';
  const messageAreaColumnIds = useMemo(
    () => getMessageAreaColumnIds(toHybridWidgetMode(messagesWidgetMode)),
    [messagesWidgetMode],
  );
  const activeDraggedWidgetSourceColumn = useMemo(() => {
    if (!activeDraggedWidget) return null;
    return resolveClassicTwoColumnsContainer(
      normalizeClassicTwoColumnsLayout(widgetLayout, belowMessagesLayout),
      activeDraggedWidget,
      toHybridWidgetMode(messagesWidgetMode),
    );
  }, [activeDraggedWidget, belowMessagesLayout, messagesWidgetMode, widgetLayout]);

  const renderWidgetCard = (widgetId: DialerWidgetId) => (
    <SortableWidgetShell key={widgetId} widgetId={widgetId} dragLocked={isWidgetDragLocked}>
      {(dragHandleProps) => {
        if (widgetId === 'voice_transcript') {
          const isCollapsed = collapsedSections[WIDGET_TO_PANEL_KEY[widgetId]];
          return (
            <div className={AI_CARD_CLASS}>
              <div className="mb-2 flex flex-wrap items-start justify-between gap-2 sm:mb-3 sm:items-center">
                <button
                  type="button"
                  onClick={() => toggleSection('transcript')}
                  className={AI_CARD_HEADER_BUTTON_CLASS}
                >
                  <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                    record_voice_over
                  </span>
                  <span className="text-sm font-medium sm:text-sm">Voice Transcript</span>
                  <span className="rounded-full bg-background/70 px-1.5 py-0.5 text-xs text-muted-foreground sm:px-2">
                    {transcriptEvents.length}
                  </span>
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-xs sm:px-2 ${
                      transcriptLiveHealth.status === 'live'
                        ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                        : transcriptLiveHealth.status === 'stale' ||
                            transcriptLiveHealth.status === 'missing'
                          ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
                          : transcriptLiveHealth.status === 'waiting'
                            ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                            : 'bg-background/70 text-muted-foreground'
                    }`}
                  >
                    {transcriptLiveHealth.label}
                  </span>
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-xs sm:px-2 ${
                      transcriptTransportStatus.status === 'live'
                        ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                        : transcriptTransportStatus.status === 'connecting'
                          ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300'
                          : transcriptTransportStatus.status === 'retrying'
                            ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                            : 'bg-background/70 text-muted-foreground'
                    }`}
                  >
                    {transcriptTransportStatus.label}
                  </span>
                  <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                    {isCollapsed ? 'expand_more' : 'expand_less'}
                  </span>
                </button>
                <div className="flex w-full flex-wrap items-center justify-end gap-1 sm:w-auto sm:gap-2">
                  {!isWidgetDragLocked ? (
                    <button
                      type="button"
                      {...dragHandleProps}
                      aria-label="Drag Voice Transcript block"
                      className="flex h-6 w-6 cursor-grab items-center justify-center rounded-full border border-border bg-background/70 text-muted-foreground transition-colors hover:text-foreground active:cursor-grabbing sm:h-7 sm:w-7"
                    >
                      <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                        drag_indicator
                      </span>
                    </button>
                  ) : null}
                  <FeedbackControls
                    disabled={!activeConversationId || isFeedbackSubmitting}
                    signal={getFeedbackSignal('voice_transcript', transcriptEvents[0]?.id)}
                    onFeedback={(signal) =>
                      handleFeedback('voice_transcript', signal, transcriptEvents[0]?.id)
                    }
                  />
                </div>
              </div>

              {!isCollapsed ? (
                <div className="max-h-44 overflow-y-auto pr-1">
                  <p className="mb-1 text-xs text-muted-foreground">
                    {transcriptLiveHealth.detail}
                  </p>
                  <p className="mb-2 text-xs text-muted-foreground">
                    {transcriptTransportStatus.detail}
                  </p>
                  {transcriptEvents.length === 0 ? (
                    <p className="text-xs italic text-muted-foreground">
                      Live transcript is stored to the timeline. Run AI refresh manually or after
                      call end.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {transcriptEvents.map((event) => (
                        <div key={event.id} className={`${AI_CARD_ITEM_CLASS} px-3 py-2`}>
                          <p className="text-xs text-foreground">{event.body}</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {formatTimestamp(event.createdAt)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          );
        }

        if (widgetId === 'action_items') {
          const isCollapsed = collapsedSections[WIDGET_TO_PANEL_KEY[widgetId]];
          return (
            <div className={AI_CARD_CLASS}>
              <div className="mb-2 flex flex-wrap items-start justify-between gap-2 sm:mb-3 sm:items-center">
                <button
                  type="button"
                  onClick={() => toggleSection('actionItems')}
                  className={AI_CARD_HEADER_BUTTON_CLASS}
                >
                  <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                    task_alt
                  </span>
                  <span className="text-sm font-medium sm:text-sm">Action Items</span>
                  <span className="rounded-full bg-background/70 px-1.5 py-0.5 text-xs text-muted-foreground sm:px-2">
                    {actionItemEvents.length}
                  </span>
                  <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                    {isCollapsed ? 'expand_more' : 'expand_less'}
                  </span>
                </button>
                <div className="flex w-full flex-wrap items-center justify-end gap-1 sm:w-auto sm:gap-2">
                  {!isWidgetDragLocked ? (
                    <button
                      type="button"
                      {...dragHandleProps}
                      aria-label="Drag Action Items block"
                      className="flex h-6 w-6 cursor-grab items-center justify-center rounded-full border border-border bg-background/70 text-muted-foreground transition-colors hover:text-foreground active:cursor-grabbing sm:h-7 sm:w-7"
                    >
                      <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                        drag_indicator
                      </span>
                    </button>
                  ) : null}
                  <FeedbackControls
                    disabled={!activeConversationId || isFeedbackSubmitting}
                    signal={getFeedbackSignal('action_item', actionItemEvents[0]?.id)}
                    onFeedback={(signal) =>
                      handleFeedback('action_item', signal, actionItemEvents[0]?.id)
                    }
                  />
                </div>
              </div>

              {!isCollapsed ? (
                <div className="max-h-40 overflow-y-auto pr-1">
                  {actionItemEvents.length === 0 ? (
                    <p className="text-xs italic text-muted-foreground">
                      Action items are generated on call end or manual AI refresh.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {actionItemEvents.map((event) => (
                        <li
                          key={event.id}
                          className={`${AI_CARD_ITEM_CLASS} px-3 py-2 text-xs text-foreground`}
                        >
                          {event.body}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>
          );
        }

        if (widgetId === 'ai_summary') {
          const isCollapsed = collapsedSections[WIDGET_TO_PANEL_KEY[widgetId]];
          return (
            <div className={`w-full ${AI_CARD_CLASS}`}>
              <div className="mb-2 flex flex-wrap items-start justify-between gap-2 sm:items-center">
                <button
                  type="button"
                  onClick={() => toggleSection('summary')}
                  className={AI_CARD_HEADER_BUTTON_CLASS}
                >
                  <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                    auto_awesome
                  </span>
                  <span className="text-sm font-medium sm:text-sm">AI Summary</span>
                  <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                    {isCollapsed ? 'expand_more' : 'expand_less'}
                  </span>
                </button>
                <div className="flex w-full flex-wrap items-center justify-end gap-1 sm:w-auto sm:gap-2">
                  {showSummaryRefreshButton ? (
                    <Button
                      variant="ghost"
                      onClick={() => regenerateInsightsMutation.mutate()}
                      disabled={!activeConversationId || regenerateInsightsMutation.isPending}
                      className={AI_REFRESH_BUTTON_CLASS}
                    >
                      {regenerateInsightsMutation.isPending ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  ) : null}
                  {!isWidgetDragLocked ? (
                    <button
                      type="button"
                      {...dragHandleProps}
                      aria-label="Drag AI Summary block"
                      className="flex h-6 w-6 cursor-grab items-center justify-center rounded-full border border-border bg-background/70 text-muted-foreground transition-colors hover:text-foreground active:cursor-grabbing sm:h-7 sm:w-7"
                    >
                      <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                        drag_indicator
                      </span>
                    </button>
                  ) : null}
                  <FeedbackControls
                    disabled={!activeConversationId || isFeedbackSubmitting}
                    signal={getFeedbackSignal('summary', summaryEvents[0]?.id)}
                    onFeedback={(signal) => handleFeedback('summary', signal, summaryEvents[0]?.id)}
                  />
                </div>
              </div>
              {!isCollapsed ? (
                <div className="max-h-36 overflow-y-auto pr-1">
                  <p className="text-xs text-foreground">{summaryText}</p>
                </div>
              ) : null}
              {regenerateInsightsMutation.isError ? (
                <p className="mt-2 text-xs text-red-400">
                  {(regenerateInsightsMutation.error as Error).message}
                </p>
              ) : null}
            </div>
          );
        }

        if (widgetId === 'rag_suggestions') {
          const isCollapsed = collapsedSections[WIDGET_TO_PANEL_KEY[widgetId]];
          return (
            <div className={`w-full ${AI_CARD_CLASS}`}>
              <div className="mb-2 flex flex-wrap items-start justify-between gap-2 sm:items-center">
                <button
                  type="button"
                  onClick={() => toggleSection('suggestions')}
                  className={AI_CARD_HEADER_BUTTON_CLASS}
                >
                  <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                    lightbulb
                  </span>
                  <span className="text-sm font-medium sm:text-sm">Suggestions</span>
                  <span className="rounded-full bg-background/70 px-1.5 py-0.5 text-xs text-muted-foreground sm:px-2">
                    {ragSuggestions.length}
                  </span>
                  <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                    {isCollapsed ? 'expand_more' : 'expand_less'}
                  </span>
                </button>
                <div className="flex w-full flex-wrap items-center justify-end gap-1 sm:w-auto sm:gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => generateRagSuggestionsMutation.mutate()}
                    disabled={
                      !activeConversationId ||
                      generateRagSuggestionsMutation.isPending ||
                      ragSuggestionsQuery.isFetching
                    }
                    className={AI_REFRESH_BUTTON_CLASS}
                  >
                    {generateRagSuggestionsMutation.isPending ? 'Generating...' : 'Suggest'}
                  </Button>
                  {!isWidgetDragLocked ? (
                    <button
                      type="button"
                      {...dragHandleProps}
                      aria-label="Drag Suggestions block"
                      className="flex h-6 w-6 cursor-grab items-center justify-center rounded-full border border-border bg-background/70 text-muted-foreground transition-colors hover:text-foreground active:cursor-grabbing sm:h-7 sm:w-7"
                    >
                      <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                        drag_indicator
                      </span>
                    </button>
                  ) : null}
                </div>
              </div>

              {!isCollapsed ? (
                <div className="space-y-2">
                  <div className="rounded-lg border border-border bg-background/60 p-2">
                    <TextArea
                      value={ragPrompt}
                      onChange={(event) => setRagPrompt(event.target.value)}
                      className="min-h-14 bg-background text-xs"
                      placeholder="Optional prompt, e.g. suggest a concise follow-up and confirm next steps."
                    />
                    <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span>Prompt is optional unless manual-only mode is enabled.</span>
                      <span>{ragPrompt.trim().length} chars</span>
                    </div>
                  </div>

                  {!activeConversationId ? (
                    <p className="text-xs text-muted-foreground">
                      Select a conversation to generate contextual suggestions.
                    </p>
                  ) : ragSuggestionsQuery.isError ? (
                    <p className="text-xs text-red-400">
                      {(ragSuggestionsQuery.error as Error).message}
                    </p>
                  ) : ragSuggestions.length === 0 ? (
                    <p className="text-xs italic text-muted-foreground">
                      No suggestions yet. Generate a batch from current conversation context.
                    </p>
                  ) : (
                    <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                      {ragSuggestions.map((suggestion) => {
                        const hasFinalStatus =
                          suggestion.status === 'accepted' || suggestion.status === 'rejected';
                        return (
                          <div
                            key={suggestion.id}
                            className={`${AI_CARD_ITEM_CLASS} space-y-2 px-3 py-2 text-xs text-foreground`}
                          >
                            <div className="flex flex-wrap items-center justify-between gap-1.5">
                              <span
                                className={`rounded-full px-2 py-0.5 text-xs font-medium ${getRagSuggestionStatusClass(suggestion.status)}`}
                              >
                                {formatRagSuggestionStatus(suggestion.status)}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                Confidence {Math.round(suggestion.confidence * 100)}%
                              </span>
                            </div>

                            <p>{suggestion.suggestionText}</p>
                            {suggestion.rationale ? (
                              <p className="text-xs text-muted-foreground">
                                Why: {suggestion.rationale}
                              </p>
                            ) : null}
                            {suggestion.evidence.length > 0 ? (
                              <ul className="space-y-1 text-xs text-muted-foreground">
                                {suggestion.evidence.slice(0, 2).map((item, index) => (
                                  <li key={`${suggestion.id}-evidence-${index}`}>• {item}</li>
                                ))}
                              </ul>
                            ) : null}

                            <div className="flex flex-wrap gap-1.5">
                              <Button
                                variant="ghost"
                                onClick={() => handleApplySuggestion(suggestion)}
                                className="h-7 rounded border border-border bg-background/80 px-2 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                              >
                                Apply
                              </Button>
                              <Button
                                variant="ghost"
                                onClick={() =>
                                  handleRagSuggestionFeedback({
                                    suggestionId: suggestion.id,
                                    signal: 'accept',
                                  })
                                }
                                disabled={isRagFeedbackPending || hasFinalStatus}
                                className="h-7 rounded border border-emerald-500/35 bg-emerald-500/10 px-2 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-500/20 dark:text-emerald-300 disabled:opacity-50"
                              >
                                Accept
                              </Button>
                              <Button
                                variant="ghost"
                                onClick={() =>
                                  handleRagSuggestionFeedback({
                                    suggestionId: suggestion.id,
                                    signal: 'reject',
                                  })
                                }
                                disabled={isRagFeedbackPending || hasFinalStatus}
                                className="h-7 rounded border border-rose-500/35 bg-rose-500/10 px-2 text-xs font-medium text-rose-700 transition-colors hover:bg-rose-500/20 dark:text-rose-300 disabled:opacity-50"
                              >
                                Reject
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : null}

              {generateRagSuggestionsMutation.isError ? (
                <p className="mt-2 text-xs text-red-400">
                  {(generateRagSuggestionsMutation.error as Error).message}
                </p>
              ) : null}
            </div>
          );
        }

        const isCollapsed = collapsedSections[WIDGET_TO_PANEL_KEY[widgetId]];
        return (
          <div className={`w-full ${AI_CARD_CLASS}`}>
            <div className="mb-2 flex flex-wrap items-start justify-between gap-2 sm:items-center">
              <button
                type="button"
                onClick={() => toggleSection('highlights')}
                className={AI_CARD_HEADER_BUTTON_CLASS}
              >
                <span className="material-symbols-outlined text-[14px] sm:text-[16px]">star</span>
                <span className="text-sm font-medium sm:text-sm">Conversation Highlights</span>
                <span className="rounded-full bg-background/70 px-1.5 py-0.5 text-xs text-muted-foreground sm:px-2">
                  {highlights.length}
                </span>
                <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                  {isCollapsed ? 'expand_more' : 'expand_less'}
                </span>
              </button>
              <div className="flex w-full flex-wrap items-center justify-end gap-1 sm:w-auto sm:gap-2">
                {!isWidgetDragLocked ? (
                  <button
                    type="button"
                    {...dragHandleProps}
                    aria-label="Drag Conversation Highlights block"
                    className="flex h-6 w-6 cursor-grab items-center justify-center rounded-full border border-border bg-background/70 text-muted-foreground transition-colors hover:text-foreground active:cursor-grabbing sm:h-7 sm:w-7"
                  >
                    <span className="material-symbols-outlined text-[14px] sm:text-[16px]">
                      drag_indicator
                    </span>
                  </button>
                ) : null}
                <FeedbackControls
                  disabled={!activeConversationId || isFeedbackSubmitting}
                  signal={getFeedbackSignal('highlight', highlightEvents[0]?.id)}
                  onFeedback={(signal) =>
                    handleFeedback('highlight', signal, highlightEvents[0]?.id)
                  }
                />
              </div>
            </div>

            {!isCollapsed ? (
              <div className="max-h-40 overflow-y-auto pr-1">
                {highlights.length === 0 ? (
                  <p className="text-xs italic text-muted-foreground">
                    Highlights are generated on call end or manual AI refresh.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {highlights.map((highlight, idx) => (
                      <li key={`${highlight}-${idx}`} className="text-xs text-foreground">
                        • {highlight}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
          </div>
        );
      }}
    </SortableWidgetShell>
  );

  return (
    <DndContext
      sensors={dndSensors}
      collisionDetection={closestCorners}
      modifiers={[snapToGrid]}
      onDragStart={(event) => handleWidgetDragStart(String(event.active.id))}
      onDragOver={(event) =>
        handleWidgetDragOver(String(event.active.id), event.over ? String(event.over.id) : null)
      }
      onDragEnd={(event) =>
        handleWidgetDragEnd(String(event.active.id), event.over ? String(event.over.id) : null)
      }
      onDragCancel={() => setActiveDraggedWidget(null)}
    >
      <div
        ref={splitContainerRef}
        className={`flex h-[calc(100vh-0.5rem)] min-h-0 overflow-hidden max-lg:h-auto max-lg:flex-col max-lg:gap-1 ${
          activeDraggedWidget ? 'select-none' : ''
        }`}
      >
        <div
          data-dialer-parent-column="left"
          className="flex min-h-0 flex-col rounded-2xl border border-border bg-card p-2 sm:p-2.5 lg:min-w-[20%] lg:shrink-0 max-lg:min-w-0"
          style={leftPanelStyle}
        >
          <div className="rounded-xl border border-border bg-card p-2 sm:p-2.5">
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    isRegistered
                      ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {isRegistered ? 'Ready' : 'Connecting...'}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    networkReadiness.ready
                      ? 'bg-brand-500/20 text-brand-700 dark:text-brand-300'
                      : 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                  }`}
                >
                  Network {networkReadiness.ready ? 'Ready' : 'Check'}
                </span>
                <button
                  type="button"
                  aria-pressed={inboundAvailabilityEnabled}
                  aria-label={inboundAvailabilityLabel}
                  disabled={
                    voiceAvailabilityQuery.isPending || saveVoiceAvailabilityMutation.isPending
                  }
                  onClick={() => saveVoiceAvailabilityMutation.mutate(!inboundAvailabilityEnabled)}
                  className={`rounded-full px-2 py-0.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    inboundAvailabilityEnabled
                      ? 'bg-sky-500/20 text-sky-700 dark:text-sky-300'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {inboundAvailabilityEnabled ? 'Inbound On' : 'Inbound Off'}
                </button>
              </div>
            </div>

            <p className="mb-2 text-xs text-muted-foreground">{inboundEligibilityLabel}</p>

            {dialerPreferenceSyncWarning ? (
              <p
                role="status"
                aria-live="polite"
                className="mb-2 rounded-lg border border-amber-400/50 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-800 dark:text-amber-200"
              >
                {dialerPreferenceSyncWarning}
              </p>
            ) : null}

            {!hiddenSections.connectionStatus ? (
              <div className="mb-2 rounded-lg border border-border bg-background/80 px-2.5 py-1.5 text-xs text-muted-foreground">
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <span>Connection: {networkReadiness.connectionLabel}</span>
                  <span
                    className={
                      connectionProfileCheck?.ok
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-amber-600 dark:text-amber-300'
                    }
                  >
                    {connectionProfileCheck?.ok
                      ? 'Profile suitable for HD path'
                      : 'Constrained profile detected'}
                  </span>
                </div>
                {unmetRequiredChecks.length > 0 ? (
                  <p className="mt-1 text-amber-700 dark:text-amber-300">
                    Missing: {unmetRequiredChecks.map((check) => check.label).join(', ')}
                  </p>
                ) : (
                  <p className="mt-1 text-emerald-700 dark:text-emerald-400">
                    All required checks passed for outbound calling.
                  </p>
                )}
              </div>
            ) : null}

            {dialerRoutingReadiness ? (
              <RoutingReadinessStrip
                readiness={dialerRoutingReadiness}
                title="Routing readiness"
                statuses={[
                  {
                    label: 'Inbound availability',
                    value: inboundAvailabilityEnabled ? 'On' : 'Off',
                    tone: inboundAvailabilityEnabled ? 'good' : 'watch',
                  },
                  {
                    label: 'Live transport',
                    value: transcriptTransportStatus.label,
                    tone: resolveLiveTransportTone(transcriptTransportStatus.status),
                  },
                  {
                    label: 'Transcript',
                    value: transcriptLiveHealth.label,
                    tone: resolveTranscriptHealthTone(transcriptLiveHealth.status),
                  },
                ]}
                className="mb-2"
              />
            ) : null}

            <div className="relative mb-2">
              <input
                ref={phoneInputRef}
                type="tel"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-lg tracking-wider text-foreground placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 sm:px-3.5 sm:py-2.5 sm:text-xl"
                placeholder="Type phone number"
                value={phoneNumber}
                onFocus={() => setIsPhoneInputFocused(true)}
                onBlur={() => setTimeout(() => setIsPhoneInputFocused(false), 80)}
                onChange={(event) => setPhoneNumber(event.target.value)}
              />
              {showContactSuggestions ? (
                <div className="absolute left-0 right-0 top-[calc(100%+0.35rem)] z-10 overflow-hidden rounded-xl border border-border bg-popover shadow-lg">
                  {contactSuggestions.map((contact) => (
                    <button
                      key={contact.id}
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleSelectContactSuggestion(contact)}
                      className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left text-sm text-foreground transition-colors last:border-b-0 hover:bg-muted"
                    >
                      <span className="truncate">{contact.displayName || contact.phoneNumber}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {contact.phoneNumber}
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}
              {shouldShowContactsSearchHint({
                isPhoneInputFocused,
                isContactsFetching: contactsQuery.isFetching,
                dialTarget,
                hasResolvedSession,
              }) ? (
                <p className="mt-1 text-xs text-muted-foreground">Searching contacts...</p>
              ) : null}
              {dialTarget.length > 0 && !canResolveSession ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  Use E.164 (`+14155552671`) or a full 10-digit US number.
                </p>
              ) : null}
              {sessionQuery.isError ? (
                <p className="mt-1 text-xs text-red-400">{(sessionQuery.error as Error).message}</p>
              ) : null}
              {autoCallError ? (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{autoCallError}</p>
              ) : null}
            </div>

            <div className="mb-2 grid grid-cols-3 gap-1.5 sm:mb-2.5 sm:gap-1.5">
              {KEYPAD_KEYS.map((key) => (
                <button
                  key={key}
                  onClick={() => handleDigitPress(key)}
                  className="flex h-12 w-full items-center justify-center rounded-xl bg-secondary text-base font-medium text-secondary-foreground transition-colors hover:bg-secondary/80 active:scale-95 sm:h-14 sm:text-lg"
                >
                  {key}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-center gap-4">
              {phoneNumber && callStatus === 'idle' && (
                <button
                  onClick={() => setPhoneNumber(phoneNumber.slice(0, -1))}
                  aria-label="Backspace"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-muted-foreground transition-colors hover:bg-secondary/80 hover:text-foreground sm:h-12 sm:w-12"
                >
                  <span
                    aria-hidden="true"
                    className="material-symbols-outlined text-[18px] sm:text-[20px]"
                  >
                    backspace
                  </span>
                </button>
              )}
              <button
                onClick={handleCall}
                aria-label={callButtonAriaLabel}
                disabled={callStatus === 'idle' && (!networkReadiness.ready || !canResolveSession)}
                className={`flex h-12 w-12 items-center justify-center rounded-full text-white shadow-lg transition-all active:scale-95 disabled:opacity-40 sm:h-14 sm:w-14 ${callBtnClass}`}
              >
                <span
                  aria-hidden="true"
                  className="material-symbols-outlined text-[24px] sm:text-[28px]"
                >
                  {callIcon}
                </span>
              </button>
            </div>

            {callStatus !== 'idle' && (
              <div className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-border bg-background py-1.5 text-sm">
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                <span className="text-muted-foreground capitalize">
                  {callStatus.replace('-', ' ')}
                </span>
              </div>
            )}

            {showCallQualityPanel && !hiddenSections.callQuality ? (
              <div className="mt-2.5 rounded-xl border border-border bg-background/80 p-2">
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => toggleSection('callQuality')}
                    className="flex min-w-0 items-center gap-1 text-left text-xs font-semibold text-foreground"
                  >
                    <span>Call Quality</span>
                    <span className="material-symbols-outlined text-[14px]">
                      {collapsedSections.callQuality ? 'expand_more' : 'expand_less'}
                    </span>
                  </button>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${qualityLevelClass(callQuality.level)}`}
                  >
                    {qualityLevelLabel(callQuality.level)}
                  </span>
                </div>

                {!collapsedSections.callQuality ? (
                  <>
                    {!callQualityHasLiveTelemetry ? (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Live telemetry appears after call connect. Current checks are pre-call only.
                      </p>
                    ) : null}

                    <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                      <p className="text-muted-foreground">
                        MOS{' '}
                        <span className="ml-1 text-foreground">
                          {formatMetric(callQuality.mos)}
                        </span>
                      </p>
                      <p className="text-muted-foreground">
                        RTT{' '}
                        <span className="ml-1 text-foreground">
                          {formatMetric(callQuality.rttMs, 0)} ms
                        </span>
                      </p>
                      <p className="text-muted-foreground">
                        Jitter{' '}
                        <span className="ml-1 text-foreground">
                          {formatMetric(callQuality.jitterMs, 1)} ms
                        </span>
                      </p>
                      <p className="text-muted-foreground">
                        Loss{' '}
                        <span className="ml-1 text-foreground">
                          {formatMetric(callQuality.packetLossPct, 1)}%
                        </span>
                      </p>
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-2">
                      <p className="text-xs text-muted-foreground">
                        Codec:{' '}
                        <span className="text-foreground uppercase">
                          {callQuality.codec ?? '—'}
                        </span>
                      </p>
                      {callQuality.updatedAt ? (
                        <p className="text-xs text-muted-foreground">
                          {formatTimestamp(callQuality.updatedAt)}
                        </p>
                      ) : null}
                    </div>

                    {callQuality.warnings.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {callQuality.warnings.slice(0, 3).map((warning) => (
                          <span
                            key={warning.name}
                            className="rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-300"
                          >
                            {warning.name}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="mt-2 min-h-0 flex-1 overflow-y-auto pr-1">
            <WidgetColumn
              columnId="left"
              items={leftSectionItems}
              showDropHint={shouldShowColumnDropHint({
                columnId: 'left',
                itemCount: leftSectionItems.length,
                activeDraggedWidgetId: activeDraggedWidget,
                sourceColumnId: activeDraggedWidgetSourceColumn,
              })}
            >
              {leftSectionItems.map((widgetId) => renderWidgetCard(widgetId))}
            </WidgetColumn>
          </div>
        </div>

        {isDesktopLayout ? (
          <button
            type="button"
            onMouseDown={() => handleSplitResizeStart('left')}
            aria-label="Resize dialer and activity columns"
            className="group flex w-2 shrink-0 cursor-col-resize items-center justify-center"
          >
            <span
              className={`h-full w-px rounded-full transition-colors ${
                isResizingLeftSplit ? 'bg-brand-400' : 'bg-border group-hover:bg-brand-400'
              }`}
            />
          </button>
        ) : null}

        <div
          data-dialer-parent-column="center"
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card lg:flex-none lg:min-w-[20%]"
          style={centerPanelStyle}
        >
          <div className="border-b border-border px-2.5 py-2 sm:px-3 sm:py-2.5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-500/20 text-brand-400">
                  <span className="material-symbols-outlined text-[18px]">person</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {activeContact?.displayName ||
                      activeConversation?.contactName ||
                      dialTarget ||
                      'No contact selected'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {activeConversationId
                      ? `Conversation ${activeConversationId.slice(0, 8)}...`
                      : 'Outbound calls & SMS'}
                  </p>
                </div>
              </div>
              {activeContact ? (
                <Button
                  onClick={() => setIsEditingContact((value) => !value)}
                  className="h-8 rounded-lg border border-border bg-transparent px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  {isEditingContact ? 'Close Edit' : 'Edit Contact'}
                </Button>
              ) : null}
            </div>

            {isInboundRinging ? (
              <div className="mt-3 rounded-xl border border-amber-500/45 bg-amber-500/10 px-3 py-2">
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                  Incoming call is ringing
                </p>
                <p className="mt-1 text-xs text-amber-700/90 dark:text-amber-200">
                  Press the call button on the keypad panel to answer.
                </p>
              </div>
            ) : null}

            {isEditingContact && activeContact ? (
              <div className="mt-3 grid gap-2 rounded-xl border border-border bg-background/70 p-3 md:grid-cols-3">
                <Input
                  type="text"
                  className="bg-background text-sm"
                  placeholder="Name"
                  value={editContactName}
                  onChange={(event) => setEditContactName(event.target.value)}
                />
                <Input
                  type="tel"
                  className="bg-background text-sm"
                  placeholder="+1234567890"
                  value={editContactPhone}
                  onChange={(event) => setEditContactPhone(event.target.value)}
                />
                <Input
                  type="email"
                  className="bg-background text-sm"
                  placeholder="email@example.com"
                  value={editContactEmail}
                  onChange={(event) => setEditContactEmail(event.target.value)}
                />
                <div className="md:col-span-3">
                  <Button
                    onClick={handleSaveContact}
                    disabled={updateContactMutation.isPending}
                    className="h-8 rounded-lg bg-brand-500 px-3 text-xs font-medium text-white transition-colors hover:bg-brand-400 disabled:opacity-40"
                  >
                    {updateContactMutation.isPending ? 'Saving...' : 'Save Contact'}
                  </Button>
                </div>
                {updateContactMutation.isError ? (
                  <p className="text-xs text-red-400 md:col-span-3">
                    {(updateContactMutation.error as Error).message}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex min-h-0 flex-1 flex-col p-2 sm:p-2.5">
            <div className="mb-1.5 rounded-lg border border-border bg-background/70 px-1.5 py-1 sm:mb-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Quick Visibility
                </p>
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                  Hidden {hiddenSectionsCount}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap gap-0.5">
                {PAGE_PREFERENCES_ORDER.map((sectionKey) => (
                  <button
                    key={sectionKey}
                    type="button"
                    onClick={() => toggleHiddenSection(sectionKey)}
                    className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs transition-colors ${
                      hiddenSections[sectionKey]
                        ? 'border-border bg-card text-muted-foreground hover:bg-muted'
                        : 'border-brand-500/35 bg-brand-500/10 text-brand-700 hover:bg-brand-500/20 dark:text-brand-300'
                    }`}
                    aria-label={`Toggle ${HIDEABLE_SECTION_LABELS[sectionKey]}`}
                  >
                    <span className="material-symbols-outlined text-[11px] leading-none">
                      {hiddenSections[sectionKey] ? 'check_box_outline_blank' : 'check_box'}
                    </span>
                    <span>{HIDEABLE_SECTION_LABELS[sectionKey]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-border bg-background px-2.5 py-2 sm:px-3 sm:py-2.5">
              {messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground">
                  <span className="material-symbols-outlined mb-2 text-[32px] sm:text-[40px]">
                    chat
                  </span>
                  <p className="text-sm">No messages yet</p>
                  <p className="text-xs">
                    Enter a phone number and send an SMS to start persisted history.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg: Message) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                          msg.direction === 'outbound'
                            ? 'rounded-br-sm bg-brand-500 text-white'
                            : 'rounded-bl-sm bg-muted text-foreground'
                        }`}
                      >
                        {msg.body}
                        <p className="mt-1 text-right text-xs opacity-60">
                          {formatTimestamp(msg.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={feedEndRef} />
                </div>
              )}
            </div>

            {!hiddenSections.callHistory ? (
              <div className="mt-1.5 rounded-xl border border-border bg-background/80 p-2 sm:mt-2 sm:p-2.5">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => toggleSection('callHistory')}
                    className="flex min-w-0 items-center gap-1 text-left text-xs font-semibold text-foreground"
                  >
                    <span>Call History</span>
                    <span className="material-symbols-outlined text-[14px]">
                      {collapsedSections.callHistory ? 'expand_more' : 'expand_less'}
                    </span>
                  </button>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {callHistory.length}
                  </span>
                </div>
                {!collapsedSections.callHistory ? (
                  !activeConversationId ? (
                    <p className="text-xs text-muted-foreground">
                      Select a contact or enter a valid number to load call records.
                    </p>
                  ) : callsQuery.isLoading ? (
                    <p className="text-xs text-muted-foreground">Loading call history...</p>
                  ) : callsQuery.isError ? (
                    <p className="text-xs text-red-400">{(callsQuery.error as Error).message}</p>
                  ) : callHistory.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No calls yet for this conversation.
                    </p>
                  ) : (
                    <div className="max-h-44 space-y-2 overflow-y-auto pr-1">
                      {callHistory.map((call) => (
                        <div
                          key={call.id}
                          className="rounded-lg border border-border bg-card/80 p-2"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <p className="text-xs font-medium text-foreground">
                                {formatTimestamp(call.startedAt)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Duration {formatDuration(call.durationSeconds)}
                                {call.agentName ? ` · Agent ${call.agentName}` : ''}
                              </p>
                            </div>
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium uppercase ${getCallRecordStatusClass(call.status)}`}
                            >
                              {call.status.replace('_', ' ')}
                            </span>
                          </div>
                          {call.recordingPlaybackUrl ? (
                            <AuthenticatedRecordingPlayer callId={call.id} />
                          ) : (
                            <p className="mt-2 text-xs text-muted-foreground">
                              Recording unavailable for this call.
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                ) : null}
              </div>
            ) : null}

            <div className="mt-1.5 space-y-1.5 border-t border-border pt-1.5 sm:mt-2 sm:space-y-1.5 sm:pt-2">
              <p className="text-xs text-muted-foreground">
                {canResolveSession
                  ? `Sending SMS to ${dialTarget}`
                  : 'Enter a valid phone number above to send SMS'}
              </p>
              <div className="flex gap-2">
                <TextArea
                  className="min-h-16 flex-1 bg-background text-sm sm:min-h-20"
                  placeholder="Type a message..."
                  value={smsBody}
                  onChange={(event) => setSmsBody(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      handleSendSms();
                    }
                  }}
                />
                <Button
                  onClick={handleSendSms}
                  disabled={sendMutation.isPending || !smsBody.trim() || !canResolveSession}
                  className="h-auto self-stretch rounded-lg bg-brand-500 px-3 text-sm font-medium text-white transition-colors hover:bg-brand-400 sm:px-4 disabled:opacity-40"
                >
                  <span className="material-symbols-outlined text-[18px]">send</span>
                </Button>
              </div>
              {sendMutation.isError ? (
                <p className="text-xs text-red-400">{(sendMutation.error as Error).message}</p>
              ) : null}
            </div>

            <div className="mt-1.5 max-h-[34vh] overflow-y-auto pr-1 sm:mt-2 sm:max-h-[40vh]">
              <div className="grid gap-1.5 sm:gap-2 md:[grid-template-columns:repeat(2,minmax(25%,1fr))]">
                {messageAreaColumnIds.map((columnId) => (
                  <WidgetColumn
                    key={columnId}
                    columnId={columnId}
                    items={visibleClassicTwoColumnsLayout[columnId]}
                    showDropHint={shouldShowColumnDropHint({
                      columnId,
                      itemCount: visibleClassicTwoColumnsLayout[columnId].length,
                      activeDraggedWidgetId: activeDraggedWidget,
                      sourceColumnId: activeDraggedWidgetSourceColumn,
                    })}
                  >
                    {visibleClassicTwoColumnsLayout[columnId].map((widgetId) =>
                      renderWidgetCard(widgetId),
                    )}
                  </WidgetColumn>
                ))}
              </div>
            </div>
          </div>
        </div>

        {showRightParentColumn && isDesktopLayout ? (
          <button
            type="button"
            onMouseDown={() => handleSplitResizeStart('right')}
            aria-label="Resize activity and sidebar columns"
            className="group flex w-2 shrink-0 cursor-col-resize items-center justify-center"
          >
            <span
              className={`h-full w-px rounded-full transition-colors ${
                isResizingRightSplit ? 'bg-brand-400' : 'bg-border group-hover:bg-brand-400'
              }`}
            />
          </button>
        ) : null}

        {showRightParentColumn ? (
          <div
            data-dialer-parent-column="right"
            className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-card lg:min-w-[20%] lg:shrink-0 max-lg:min-w-0"
            style={rightPanelStyle}
          >
            <div className="min-h-0 flex-1 overflow-y-auto p-2 sm:p-2.5">
              <WidgetColumn
                columnId="right"
                items={rightSectionItems}
                showDropHint={shouldShowColumnDropHint({
                  columnId: 'right',
                  itemCount: rightSectionItems.length,
                  activeDraggedWidgetId: activeDraggedWidget,
                  sourceColumnId: activeDraggedWidgetSourceColumn,
                })}
              >
                {rightSectionItems.map((widgetId) => renderWidgetCard(widgetId))}
              </WidgetColumn>
            </div>
          </div>
        ) : null}
      </div>
    </DndContext>
  );
}
